import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { speakWithLuna, stopLunaVoice } from '../utils/lunaVoice';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './PlacementTest.css';

const allQuestionsByLanguage = {
    italian: {
        A1: [
            {
                id: 'a1_1',
                question: 'Which word means "good morning" in Italian?',
                options: ['Buonasera', 'Buonanotte', 'Buongiorno', 'Arrivederci'],
                correct: 2,
            },
            {
                id: 'a1_2',
                question: 'Complete: "Io ___ uno studente."',
                options: ['sei', 'sono', 'è', 'siamo'],
                correct: 1,
            },
            {
                id: 'a1_3',
                question: 'What does "Dove abiti?" mean?',
                options: ['What do you do?', 'How old are you?', 'Where do you live?', 'What is your name?'],
                correct: 2,
            },
            {
                id: 'a1_4',
                question: 'Which is the correct plural of "il libro"?',
                options: ['il libri', 'la libra', 'i libri', 'le libri'],
                correct: 2,
            },
            {
                id: 'a1_5',
                question: 'What does "Ho fame" mean?',
                options: ['I am thirsty', 'I am hungry', 'I am tired', 'I am cold'],
                correct: 1,
            },
            {
                id: 'a1_6',
                question: 'Which word means "thank you"?',
                options: ['Prego', 'Grazie', 'Scusa', 'Per favore'],
                correct: 1,
            },
            {
                id: 'a1_7',
                question: 'Complete: "Lei ___ italiana."',
                options: ['sono', 'sei', 'è', 'siamo'],
                correct: 2,
            },
            {
                id: 'a1_8',
                question: 'What is the correct article for "casa" (house)?',
                options: ['il', 'lo', 'la', 'i'],
                correct: 2,
            },
            {
                id: 'a1_9',
                question: 'What does "Quanti anni hai?" mean?',
                options: ['What time is it?', 'How old are you?', 'How are you?', 'Where are you from?'],
                correct: 1,
            },
            {
                id: 'a1_10',
                question: 'Which sentence correctly says "I have two brothers"?',
                options: ['Ho due fratello', 'Ho due fratelli', 'Sono due fratelli', 'Ho due sorelle'],
                correct: 1,
            },
        ],
        A2: [
            {
                id: 'a2_1',
                question: 'Which sentence correctly uses the passato prossimo?',
                options: [
                    'Ieri mangio la pizza.',
                    'Ieri ho mangiato la pizza.',
                    'Ieri mangiavo la pizza.',
                    'Ieri mangiai la pizza.',
                ],
                correct: 1,
            },
            {
                id: 'a2_2',
                question: 'What does "Mi piacciono le mele" mean?',
                options: ['I want apples', 'I have apples', 'I like apples', 'I need apples'],
                correct: 2,
            },
            {
                id: 'a2_3',
                question: 'Complete: "Ogni giorno ___ al lavoro in bicicletta."',
                options: ['sono andato', 'andavo', 'vado', 'andrò'],
                correct: 2,
            },
            {
                id: 'a2_4',
                question: 'Which sentence uses the imperfetto correctly?',
                options: [
                    'Da bambino, ho giocato ogni giorno.',
                    'Da bambino, giocavo ogni giorno.',
                    'Da bambino, gioco ogni giorno.',
                    'Da bambino, giocherò ogni giorno.',
                ],
                correct: 1,
            },
            {
                id: 'a2_5',
                question: 'What is the correct form of "andare" for "voi"?',
                options: ['andate', 'andano', 'andiamo', 'vai'],
                correct: 0,
            },
            {
                id: 'a2_6',
                question: 'Complete: "Domani ___ a Roma."',
                options: ['vado', 'andavo', 'sono andato', 'andassi'],
                correct: 0,
            },
            {
                id: 'a2_7',
                question: 'What does "Devo studiare stasera" mean?',
                options: ['I studied last night', 'I have to study tonight', 'I want to study tonight', 'I was studying tonight'],
                correct: 1,
            },
            {
                id: 'a2_8',
                question: 'Which is the correct comparative: "Marco è ___ alto di me."',
                options: ['molto', 'più', 'tanto', 'meno di'],
                correct: 1,
            },
            {
                id: 'a2_9',
                question: 'Complete: "Lei si sveglia ___ le sette ogni mattina."',
                options: ['a', 'alle', 'di', 'in'],
                correct: 1,
            },
            {
                id: 'a2_10',
                question: 'What does "Non ho ancora finito" mean?',
                options: ['I have already finished', 'I never finished', 'I haven\'t finished yet', 'I will finish soon'],
                correct: 2,
            },
        ],
        B1: [
            {
                id: 'b1_1',
                question: 'Choose the correct form: "Spero che lui ___ presto."',
                options: ['viene', 'venga', 'venisse', 'è venuto'],
                correct: 1,
            },
            {
                id: 'b1_2',
                question: 'Complete: "Se avessi più tempo, ___ in palestra."',
                options: ['vado', 'andrei', 'andavo', 'sono andato'],
                correct: 1,
            },
            {
                id: 'b1_3',
                question: 'Which sentence correctly uses the congiuntivo?',
                options: [
                    'Penso che Marco ha ragione.',
                    'Penso che Marco abbia ragione.',
                    'Penso che Marco aveva ragione.',
                    'Penso che Marco avrà ragione.',
                ],
                correct: 1,
            },
            {
                id: 'b1_4',
                question: 'What does "nonostante" mean?',
                options: ['therefore', 'however', 'although', 'unless'],
                correct: 2,
            },
            {
                id: 'b1_5',
                question: 'Complete: "Il treno ___ quando siamo arrivati alla stazione."',
                options: ['è già partito', 'partiva già', 'partirà già', 'già parte'],
                correct: 0,
            },
            {
                id: 'b1_6',
                question: 'Which sentence correctly uses an indirect object pronoun?',
                options: [
                    'Lo ho scritto una lettera.',
                    'Gli ho scritto una lettera.',
                    'Lo ho scritto a lui una lettera.',
                    'Ha scritto gli una lettera.',
                ],
                correct: 1,
            },
            {
                id: 'b1_7',
                question: 'What does "a meno che" mean?',
                options: ['as soon as', 'unless', 'because', 'even though'],
                correct: 1,
            },
            {
                id: 'b1_8',
                question: 'Complete: "Mentre ___ la cena, ha squillato il telefono."',
                options: ['preparo', 'ho preparato', 'preparavo', 'preparerò'],
                correct: 2,
            },
            {
                id: 'b1_9',
                question: 'Which sentence best expresses "I would have liked to go"?',
                options: [
                    'Vorrei andare.',
                    'Sarei andato.',
                    'Avrei voluto andare.',
                    'Volevo andare.',
                ],
                correct: 2,
            },
            {
                id: 'b1_10',
                question: 'What does "appena" mean in: "Appena arrivo, ti chiamo"?',
                options: ['maybe', 'as soon as', 'usually', 'almost'],
                correct: 1,
            },
        ],
        B2: [
            {
                id: 'b2_1',
                question: 'Which sentence uses the subjunctive correctly after "sebbene"?',
                options: [
                    'Sebbene sono stanco, continuo a lavorare.',
                    'Sebbene ero stanco, continuo a lavorare.',
                    'Sebbene sia stanco, continuo a lavorare.',
                    'Sebbene sarò stanco, continuo a lavorare.',
                ],
                correct: 2,
            },
            {
                id: 'b2_2',
                question: 'What does "a meno che non" mean?',
                options: ['as long as', 'unless', 'although', 'provided that'],
                correct: 1,
            },
            {
                id: 'b2_3',
                question: 'Choose the correct passive construction: "Il rapporto ___ dal direttore ieri."',
                options: [
                    'è stato firmato',
                    'ha firmato',
                    'viene firmando',
                    'si è firmato',
                ],
                correct: 0,
            },
            {
                id: 'b2_4',
                question: 'Complete: "Se fossi partito prima, ___ in tempo."',
                options: [
                    'sarei arrivato',
                    'arriverei',
                    'sono arrivato',
                    'arrivassi',
                ],
                correct: 0,
            },
            {
                id: 'b2_5',
                question: 'Which word best replaces "molto grande" in formal writing?',
                options: ['grandissimo', 'enorme', 'tanto grande', 'più grande'],
                correct: 1,
            },
            {
                id: 'b2_6',
                question: 'Which sentence correctly uses "affinché"?',
                options: [
                    'Studio affinché passo l\'esame.',
                    'Studio affinché passi l\'esame.',
                    'Studio affinché ho passato l\'esame.',
                    'Studio affinché passerò l\'esame.',
                ],
                correct: 1,
            },
            {
                id: 'b2_7',
                question: 'What nuance does "bensì" add compared to "ma"?',
                options: [
                    'It means "and" instead of "but".',
                    'It introduces a stronger contrast, meaning "but rather".',
                    'It is used only in questions.',
                    'It softens the contrast, meaning "perhaps".',
                ],
                correct: 1,
            },
            {
                id: 'b2_8',
                question: 'Complete: "Qualunque cosa ___ , ti sosterrò."',
                options: ['decidi', 'decida', 'deciderai', 'hai deciso'],
                correct: 1,
            },
            {
                id: 'b2_9',
                question: 'Which sentence is grammatically correct in formal register?',
                options: [
                    'Gli ho detto di venire con me.',
                    'Ci ho detto di venire con me.',
                    'Le ho detto di venire con me, riferendosi a un uomo.',
                    'Lo ho detto a lui di venire con me.',
                ],
                correct: 0,
            },
            {
                id: 'b2_10',
                question: 'What does "malgrado" mean?',
                options: ['because of', 'despite', 'thanks to', 'in order to'],
                correct: 1,
            },
        ],
        C1: [
            {
                id: 'c1_1',
                question: 'Which sentence is most appropriate for formal academic writing?',
                options: [
                    'Gli ho detto che non ci andavo.',
                    'Ho detto a lui che non andavo lì.',
                    'Gli comunicai che non mi sarei recato in quel luogo.',
                    'Non volevo andarci e gliel\'ho fatto sapere.',
                ],
                correct: 2,
            },
            {
                id: 'c1_2',
                question: 'What is the function of the "congiuntivo trapassato"?',
                options: [
                    'To express future hypotheticals',
                    'To express unrealised past conditions in contrary-to-fact sentences',
                    'To express polite requests in the present',
                    'To express wishes about the future',
                ],
                correct: 1,
            },
            {
                id: 'c1_3',
                question: 'Complete: "___ avessi studiato di più, avresti superato l\'esame."',
                options: ['Se', 'Quando', 'Qualora', 'Purché'],
                correct: 0,
            },
            {
                id: 'c1_4',
                question: 'Which is an example of the "periodo ipotetico dell\'impossibilità"?',
                options: [
                    'Se studio, passo l\'esame.',
                    'Se studiassi, passerei l\'esame.',
                    'Se avessi studiato, avrei passato l\'esame.',
                    'Quando studio, passo l\'esame.',
                ],
                correct: 2,
            },
            {
                id: 'c1_5',
                question: 'What nuance does "eppure" add to a sentence?',
                options: [
                    'It introduces a consequence.',
                    'It introduces a surprising contrast — "and yet" or "even so".',
                    'It introduces a condition.',
                    'It introduces an alternative.',
                ],
                correct: 1,
            },
            {
                id: 'c1_6',
                question: 'Which sentence correctly uses "qualora"?',
                options: [
                    'Qualora piove, resto a casa.',
                    'Qualora piovesse, resterei a casa.',
                    'Qualora ha piovuto, sono restato a casa.',
                    'Qualora pioverà, resterò a casa.',
                ],
                correct: 1,
            },
            {
                id: 'c1_7',
                question: 'What does "non è che" typically signal at the start of a sentence?',
                options: [
                    'A strong affirmation',
                    'A polite hedge before a mild denial or clarification',
                    'A direct command',
                    'A future prediction',
                ],
                correct: 1,
            },
            {
                id: 'c1_8',
                question: 'Which construction correctly expresses "the more..., the more..."?',
                options: [
                    'Più... più...',
                    'Tanto... quanto...',
                    'Sia... sia...',
                    'Né... né...',
                ],
                correct: 0,
            },
            {
                id: 'c1_9',
                question: 'What is the effect of using "chiunque" with the subjunctive, as in "Chiunque tu sia..."?',
                options: [
                    'It expresses certainty about someone\'s identity.',
                    'It expresses indifference to who the person might be — "whoever you are".',
                    'It is a polite greeting.',
                    'It is only used with animals.',
                ],
                correct: 1,
            },
            {
                id: 'c1_10',
                question: 'Which sentence demonstrates correct use of the "gerundio passato"?',
                options: [
                    'Avendo finito il lavoro, sono uscito.',
                    'Finendo il lavoro, sono uscito.',
                    'Avere finito il lavoro, sono uscito.',
                    'Finito il lavoro, essendo uscito.',
                ],
                correct: 0,
            },
        ],
        C2: [
            {
                id: 'c2_1',
                question: 'Which word best completes: "Il silenzio era ___, quasi capace di soffocare ogni pensiero."',
                options: ['grande', 'assordante', 'tranquillo', 'lungo'],
                correct: 1,
            },
            {
                id: 'c2_2',
                question: 'What stylistic effect does "passato remoto" create vs "passato prossimo" in formal writing?',
                options: [
                    'No difference — they are interchangeable.',
                    'It creates psychological distance, giving events a more literary or historical tone.',
                    'It is more informal and conversational.',
                    'It is only used in questions.',
                ],
                correct: 1,
            },
            {
                id: 'c2_3',
                question: 'Which sentence demonstrates the most sophisticated use of the gerundio?',
                options: [
                    'Mangiando, guardo la televisione.',
                    'Avendo già letto il romanzo, potei analizzarne le strutture narrative con maggiore profondità.',
                    'Correndo, sono arrivato tardi.',
                    'Studiando molto, ho imparato l\'italiano.',
                ],
                correct: 1,
            },
            {
                id: 'c2_4',
                question: 'What does "non vedere l\'ora" mean?',
                options: [
                    'To not have enough time',
                    'To be unable to read a clock',
                    'To look forward to something eagerly',
                    'To be running late',
                ],
                correct: 2,
            },
            {
                id: 'c2_5',
                question: 'In which context would you use "si evince" in formal Italian?',
                options: [
                    'When giving a personal opinion',
                    'When drawing a logical conclusion from evidence — "it emerges that"',
                    'When expressing doubt',
                    'When making a polite request',
                ],
                correct: 1,
            },
            {
                id: 'c2_6',
                question: 'What does the construction "non che non" convey, as in "Non che non mi piaccia..."?',
                options: [
                    'A flat denial',
                    'A rhetorical concession before qualifying a statement — "it\'s not that I don\'t..."',
                    'A simple negative statement',
                    'A question',
                ],
                correct: 1,
            },
            {
                id: 'c2_7',
                question: 'Which sentence best reflects sophisticated literary register?',
                options: [
                    'Camminava piano perché era stanco.',
                    'Camminava con passo lento, quasi a voler prolungare l\'istante.',
                    'Camminava lentamente perché stanco.',
                    'Era stanco e quindi camminava piano.',
                ],
                correct: 1,
            },
            {
                id: 'c2_8',
                question: 'What is the function of "laddove" in formal writing?',
                options: [
                    'It means "wherever" in a literal spatial sense only.',
                    'It can introduce a contrast or condition, meaning "whereas" or "in cases where".',
                    'It is used only for physical locations.',
                    'It is an informal filler word.',
                ],
                correct: 1,
            },
            {
                id: 'c2_9',
                question: 'Which idiom best conveys "to read between the lines"?',
                options: [
                    'Leggere tra le righe',
                    'Leggere ad alta voce',
                    'Dare un\'occhiata',
                    'Prendere alla lettera',
                ],
                correct: 0,
            },
            {
                id: 'c2_10',
                question: 'What subtle distinction does "bensì" carry that "ma" does not, in careful writing?',
                options: [
                    'No distinction — fully interchangeable',
                    'It replaces a preceding negative with a corrective alternative — "but rather"',
                    'It is used only at the start of a paragraph',
                    'It signals agreement rather than contrast',
                ],
                correct: 1,
            },
        ],
    },
    english: {
        A1: [
            {
                id: 'en_a1_1',
                question: 'Which sentence is correct?',
                options: ['She go to school.', 'She goes to school.', 'She going to school.', 'She gone to school.'],
                correct: 1,
            },
            {
                id: 'en_a1_2',
                question: 'Complete: "I ___ a student."',
                options: ['am', 'is', 'are', 'be'],
                correct: 0,
            },
            {
                id: 'en_a1_3',
                question: 'What does "Where do you live?" mean?',
                options: ['What do you do?', 'How old are you?', 'Where is your home?', 'What is your name?'],
                correct: 2,
            },
            {
                id: 'en_a1_4',
                question: 'Which is the correct plural of "book"?',
                options: ['bookes', 'book', 'books', 'bookies'],
                correct: 2,
            },
            {
                id: 'en_a1_5',
                question: 'What does "I am hungry" mean?',
                options: ['I am thirsty', 'I want to eat', 'I am tired', 'I am cold'],
                correct: 1,
            },
            {
                id: 'en_a1_6',
                question: 'Which word means "thank you very much"?',
                options: ['Please', 'Thanks a lot', 'Excuse me', 'You\'re welcome'],
                correct: 1,
            },
            {
                id: 'en_a1_7',
                question: 'Complete: "She ___ English."',
                options: ['speak', 'speaks', 'speaking', 'spoke'],
                correct: 1,
            },
            {
                id: 'en_a1_8',
                question: 'What is the correct article: "___ apple"?',
                options: ['a', 'an', 'the', 'no article needed'],
                correct: 1,
            },
            {
                id: 'en_a1_9',
                question: 'What does "How old are you?" ask about?',
                options: ['Time', 'Age', 'Health', 'Location'],
                correct: 1,
            },
            {
                id: 'en_a1_10',
                question: 'Which sentence correctly says "I have two brothers"?',
                options: ['I have two brother.', 'I have two brothers.', 'I has two brothers.', 'I having two brothers.'],
                correct: 1,
            },
        ],
        A2: [
            {
                id: 'en_a2_1',
                question: 'Which sentence correctly uses the past tense?',
                options: [
                    'Yesterday I eat pizza.',
                    'Yesterday I ate pizza.',
                    'Yesterday I eating pizza.',
                    'Yesterday I eaten pizza.',
                ],
                correct: 1,
            },
            {
                id: 'en_a2_2',
                question: 'What does "I like apples" mean?',
                options: ['I want apples', 'I have apples', 'Apples please me', 'I need apples'],
                correct: 2,
            },
            {
                id: 'en_a2_3',
                question: 'Complete: "Every day I ___ to work by bike."',
                options: ['went', 'was going', 'go', 'will go'],
                correct: 2,
            },
            {
                id: 'en_a2_4',
                question: 'Which sentence uses the past continuous correctly?',
                options: [
                    'As a child, I play outside every day.',
                    'As a child, I was playing outside every day.',
                    'As a child, I played outside every day.',
                    'As a child, I will play outside every day.',
                ],
                correct: 2,
            },
            {
                id: 'en_a2_5',
                question: 'What is the correct form of "go" for "we"?',
                options: ['goes', 'go', 'going', 'gone'],
                correct: 1,
            },
            {
                id: 'en_a2_6',
                question: 'Complete: "Tomorrow I ___ to Rome."',
                options: ['go', 'went', 'was going', 'will go'],
                correct: 3,
            },
            {
                id: 'en_a2_7',
                question: 'What does "I have to study tonight" mean?',
                options: ['I studied last night', 'I need to study tonight', 'I want to study tonight', 'I was studying tonight'],
                correct: 1,
            },
            {
                id: 'en_a2_8',
                question: 'Which is the correct comparative: "Mark is ___ tall than me."',
                options: ['very', 'more', 'as', 'much'],
                correct: 1,
            },
            {
                id: 'en_a2_9',
                question: 'Complete: "She wakes up ___ seven every morning."',
                options: ['in', 'at', 'on', 'to'],
                correct: 1,
            },
            {
                id: 'en_a2_10',
                question: 'What does "I haven\'t finished yet" mean?',
                options: ['I already finished', 'I never finished', 'I still need to finish', 'I will finish soon'],
                correct: 2,
            },
        ],
        B1: [
            {
                id: 'en_b1_1',
                question: 'Choose the correct form: "I hope he ___ soon."',
                options: ['comes', 'come', 'came', 'coming'],
                correct: 0,
            },
            {
                id: 'en_b1_2',
                question: 'Complete: "If I had more time, I ___ to the gym."',
                options: ['go', 'would go', 'went', 'have gone'],
                correct: 1,
            },
            {
                id: 'en_b1_3',
                question: 'Which sentence correctly uses the present perfect?',
                options: [
                    'I live here since 2015.',
                    'I have lived here since 2015.',
                    'I lived here since 2015.',
                    'I am living here since 2015.',
                ],
                correct: 1,
            },
            {
                id: 'en_b1_4',
                question: 'What does "although" mean?',
                options: ['therefore', 'however', 'even though', 'unless'],
                correct: 2,
            },
            {
                id: 'en_b1_5',
                question: 'Complete: "The train ___ already left when we arrived at the station."',
                options: ['has', 'had', 'will have', 'was'],
                correct: 1,
            },
            {
                id: 'en_b1_6',
                question: 'Which sentence correctly uses an indirect object?',
                options: [
                    'I wrote a letter him.',
                    'I wrote him a letter.',
                    'I wrote to a letter him.',
                    'I wrote a letter to him it.',
                ],
                correct: 1,
            },
            {
                id: 'en_b1_7',
                question: 'What does "unless" mean?',
                options: ['as soon as', 'if not', 'because', 'even though'],
                correct: 1,
            },
            {
                id: 'en_b1_8',
                question: 'Complete: "While I ___ dinner, the phone rang."',
                options: ['prepare', 'have prepared', 'was preparing', 'will prepare'],
                correct: 2,
            },
            {
                id: 'en_b1_9',
                question: 'Which sentence best expresses "I would have liked to go"?',
                options: [
                    'I would like to go.',
                    'I would go.',
                    'I would have liked to go.',
                    'I wanted to go.',
                ],
                correct: 2,
            },
            {
                id: 'en_b1_10',
                question: 'What does "as soon as" mean in: "As soon as I arrive, I\'ll call you"?',
                options: ['maybe', 'immediately when', 'usually', 'almost'],
                correct: 1,
            },
        ],
        B2: [
            {
                id: 'en_b2_1',
                question: 'Which sentence uses the subjunctive correctly after "although"?',
                options: [
                    'Although he is tired, he keeps working.',
                    'Although he was tired, he keeps working.',
                    'Although he tired, he keeps working.',
                    'Although he will be tired, he keeps working.',
                ],
                correct: 0,
            },
            {
                id: 'en_b2_2',
                question: 'What does "unless" mean in a formal sentence?',
                options: ['as long as', 'if not', 'although', 'provided that'],
                correct: 1,
            },
            {
                id: 'en_b2_3',
                question: 'Choose the correct passive construction: "The report ___ by the director yesterday."',
                options: [
                    'was signed',
                    'has signed',
                    'is signing',
                    'signed itself',
                ],
                correct: 0,
            },
            {
                id: 'en_b2_4',
                question: 'Complete: "If I had left earlier, I ___ on time."',
                options: [
                    'would have arrived',
                    'would arrive',
                    'arrived',
                    'had arrived',
                ],
                correct: 0,
            },
            {
                id: 'en_b2_5',
                question: 'Which word best replaces "very big" in formal writing?',
                options: ['biggish', 'enormous', 'so big', 'bigger'],
                correct: 1,
            },
            {
                id: 'en_b2_6',
                question: 'Which sentence correctly uses "so that"?',
                options: [
                    'I study so that I pass the exam.',
                    'I study so that I passed the exam.',
                    'I study so that I have passed the exam.',
                    'I study so that I will passed the exam.',
                ],
                correct: 0,
            },
            {
                id: 'en_b2_7',
                question: 'What nuance does "yet" add compared to "but"?',
                options: [
                    'It means "and" instead of "but".',
                    'It introduces a surprising contrast, similar to "and yet".',
                    'It is used only in questions.',
                    'It softens the contrast, meaning "perhaps".',
                ],
                correct: 1,
            },
            {
                id: 'en_b2_8',
                question: 'Complete: "Whatever you ___, I will support you."',
                options: ['decide', 'decided', 'will decide', 'have decided'],
                correct: 0,
            },
            {
                id: 'en_b2_9',
                question: 'Which sentence is grammatically correct in formal register?',
                options: [
                    'I told him to come with me.',
                    'I told to him to come with me.',
                    'I told him for come with me.',
                    'I told at him to come with me.',
                ],
                correct: 0,
            },
            {
                id: 'en_b2_10',
                question: 'What does "despite" mean?',
                options: ['because of', 'in spite of', 'thanks to', 'in order to'],
                correct: 1,
            },
        ],
        C1: [
            {
                id: 'en_c1_1',
                question: 'Which sentence is most appropriate for formal academic writing?',
                options: [
                    'I told him I wasn\'t going there.',
                    'I said to him that I didn\'t go there.',
                    'I informed him that I would not be attending that location.',
                    'I didn\'t want to go there and I let him know.',
                ],
                correct: 2,
            },
            {
                id: 'en_c1_2',
                question: 'What is the function of the past perfect in narrative writing?',
                options: [
                    'To express future hypotheticals',
                    'To show an action that happened before another past action',
                    'To express polite requests in the present',
                    'To express wishes about the future',
                ],
                correct: 1,
            },
            {
                id: 'en_c1_3',
                question: 'Complete: "___ I had studied more, I would have passed the exam."',
                options: ['If', 'When', 'Should', 'Unless'],
                correct: 0,
            },
            {
                id: 'en_c1_4',
                question: 'Which is an example of the third conditional (impossible past)?',
                options: [
                    'If I study, I pass the exam.',
                    'If I studied, I would pass the exam.',
                    'If I had studied, I would have passed the exam.',
                    'When I study, I pass the exam.',
                ],
                correct: 2,
            },
            {
                id: 'en_c1_5',
                question: 'What nuance does "yet still" add to a sentence?',
                options: [
                    'It introduces a consequence.',
                    'It emphasizes a persisting contrast — "and yet, even so".',
                    'It introduces a condition.',
                    'It introduces an alternative.',
                ],
                correct: 1,
            },
            {
                id: 'en_c1_6',
                question: 'Which sentence correctly uses "were it not for"?',
                options: [
                    'Were it not for your help, I fail.',
                    'Were it not for your help, I would have failed.',
                    'Were it not for your help, I have failed.',
                    'Were it not for your help, I will fail.',
                ],
                correct: 1,
            },
            {
                id: 'en_c1_7',
                question: 'What does "not that" typically signal at the start of a sentence?',
                options: [
                    'A strong affirmation',
                    'A polite hedge before a mild denial or clarification',
                    'A direct command',
                    'A future prediction',
                ],
                correct: 1,
            },
            {
                id: 'en_c1_8',
                question: 'Which construction correctly expresses "the more..., the more..."?',
                options: [
                    'The more..., the more...',
                    'As much..., as much...',
                    'Both..., and...',
                    'Neither..., nor...',
                ],
                correct: 0,
            },
            {
                id: 'en_c1_9',
                question: 'What is the effect of using "whoever" with a general clause, as in "Whoever you are..."?',
                options: [
                    'It expresses certainty about someone\'s identity.',
                    'It expresses indifference to who the person might be.',
                    'It is a polite greeting.',
                    'It is only used with animals.',
                ],
                correct: 1,
            },
            {
                id: 'en_c1_10',
                question: 'Which sentence demonstrates correct use of a perfect participle clause?',
                options: [
                    'Having finished the work, I went out.',
                    'Finishing the work, I went out.',
                    'Have finished the work, I went out.',
                    'Finished the work, having went out.',
                ],
                correct: 0,
            },
        ],
        C2: [
            {
                id: 'en_c2_1',
                question: 'Which word best completes: "The silence was ___, almost capable of smothering every thought."',
                options: ['large', 'deafening', 'calm', 'long'],
                correct: 1,
            },
            {
                id: 'en_c2_2',
                question: 'What stylistic effect does the passive voice create in formal or academic writing?',
                options: [
                    'No difference from active voice.',
                    'It shifts focus away from the agent, creating a more objective or detached tone.',
                    'It is more informal and conversational.',
                    'It is only used in questions.',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_3',
                question: 'Which sentence demonstrates the most sophisticated use of a participle clause?',
                options: [
                    'Eating, I watch television.',
                    'Having already read the novel, I was able to analyze its narrative structures with greater depth.',
                    'Running, I arrived late.',
                    'Studying a lot, I learned English.',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_4',
                question: 'What does "to look forward to something" mean?',
                options: [
                    'To not have enough time',
                    'To be unable to see clearly',
                    'To anticipate something eagerly',
                    'To be running late',
                ],
                correct: 2,
            },
            {
                id: 'en_c2_5',
                question: 'In which context would you use "it can be inferred that" in formal English?',
                options: [
                    'When giving a personal opinion',
                    'When drawing a logical conclusion from evidence',
                    'When expressing doubt',
                    'When making a polite request',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_6',
                question: 'What does the construction "not that... but rather" convey?',
                options: [
                    'A flat denial',
                    'A rhetorical correction, replacing one idea with a more accurate one',
                    'A simple negative statement',
                    'A question',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_7',
                question: 'Which sentence best reflects sophisticated literary register?',
                options: [
                    'He walked slowly because he was tired.',
                    'He walked with a measured gait, as though reluctant to let the moment pass.',
                    'He walked slowly since he was tired.',
                    'He was tired, so he walked slowly.',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_8',
                question: 'What is the function of "whereby" in formal writing?',
                options: [
                    'It means "wherever" in a literal spatial sense only.',
                    'It introduces a clause explaining the means or manner by which something happens.',
                    'It is used only for physical locations.',
                    'It is an informal filler word.',
                ],
                correct: 1,
            },
            {
                id: 'en_c2_9',
                question: 'Which idiom best conveys "to understand a deeper or hidden meaning"?',
                options: [
                    'To read between the lines',
                    'To read out loud',
                    'To take a glance',
                    'To take something literally',
                ],
                correct: 0,
            },
            {
                id: 'en_c2_10',
                question: 'What subtle distinction does "albeit" carry compared to "but", in careful writing?',
                options: [
                    'No distinction — fully interchangeable',
                    'It concedes a point while introducing a qualifying contrast, similar to "although" but more concise',
                    'It is used only at the start of a paragraph',
                    'It signals agreement rather than contrast',
                ],
                correct: 1,
            },
        ],
    },
    spanish: {
        A1: [
            {
                id: 'es_a1_1',
                question: 'Which word means "good morning" in Spanish?',
                options: ['Buenas noches', 'Buenas tardes', 'Buenos días', 'Adiós'],
                correct: 2,
            },
            {
                id: 'es_a1_2',
                question: 'Complete: "Yo ___ estudiante."',
                options: ['eres', 'soy', 'es', 'somos'],
                correct: 1,
            },
            {
                id: 'es_a1_3',
                question: 'What does "¿Dónde vives?" mean?',
                options: ['What do you do?', 'How old are you?', 'Where do you live?', 'What is your name?'],
                correct: 2,
            },
            {
                id: 'es_a1_4',
                question: 'Which is the correct plural of "el libro"?',
                options: ['el libros', 'la libra', 'los libros', 'las libros'],
                correct: 2,
            },
            {
                id: 'es_a1_5',
                question: 'What does "Tengo hambre" mean?',
                options: ['I am thirsty', 'I am hungry', 'I am tired', 'I am cold'],
                correct: 1,
            },
            {
                id: 'es_a1_6',
                question: 'Which word means "thank you"?',
                options: ['De nada', 'Gracias', 'Perdón', 'Por favor'],
                correct: 1,
            },
            {
                id: 'es_a1_7',
                question: 'Complete: "Ella ___ española."',
                options: ['soy', 'eres', 'es', 'somos'],
                correct: 2,
            },
            {
                id: 'es_a1_8',
                question: 'What is the correct article for "casa" (house)?',
                options: ['el', 'lo', 'la', 'los'],
                correct: 2,
            },
            {
                id: 'es_a1_9',
                question: 'What does "¿Cuántos años tienes?" mean?',
                options: ['What time is it?', 'How old are you?', 'How are you?', 'Where are you from?'],
                correct: 1,
            },
            {
                id: 'es_a1_10',
                question: 'Which sentence correctly says "I have two brothers"?',
                options: ['Tengo dos hermano', 'Tengo dos hermanos', 'Soy dos hermanos', 'Tengo dos hermanas'],
                correct: 1,
            },
        ],
        A2: [
            {
                id: 'es_a2_1',
                question: 'Which sentence correctly uses the pretérito perfecto?',
                options: [
                    'Ayer como pizza.',
                    'Ayer he comido pizza.',
                    'Ayer comía pizza.',
                    'Ayer comí pizza.',
                ],
                correct: 3,
            },
            {
                id: 'es_a2_2',
                question: 'What does "Me gustan las manzanas" mean?',
                options: ['I want apples', 'I have apples', 'I like apples', 'I need apples'],
                correct: 2,
            },
            {
                id: 'es_a2_3',
                question: 'Complete: "Todos los días ___ al trabajo en bicicleta."',
                options: ['fui', 'iba', 'voy', 'iré'],
                correct: 2,
            },
            {
                id: 'es_a2_4',
                question: 'Which sentence uses the imperfecto correctly?',
                options: [
                    'De niño, he jugado todos los días.',
                    'De niño, jugaba todos los días.',
                    'De niño, juego todos los días.',
                    'De niño, jugaré todos los días.',
                ],
                correct: 1,
            },
            {
                id: 'es_a2_5',
                question: 'What is the correct form of "ir" for "vosotros"?',
                options: ['vais', 'van', 'vamos', 'vas'],
                correct: 0,
            },
            {
                id: 'es_a2_6',
                question: 'Complete: "Mañana ___ a Madrid."',
                options: ['voy', 'iba', 'he ido', 'fui'],
                correct: 0,
            },
            {
                id: 'es_a2_7',
                question: 'What does "Tengo que estudiar esta noche" mean?',
                options: ['I studied last night', 'I have to study tonight', 'I want to study tonight', 'I was studying tonight'],
                correct: 1,
            },
            {
                id: 'es_a2_8',
                question: 'Which is the correct comparative: "Marco es ___ alto que yo."',
                options: ['muy', 'más', 'tan', 'menos de'],
                correct: 1,
            },
            {
                id: 'es_a2_9',
                question: 'Complete: "Ella se despierta ___ las siete cada mañana."',
                options: ['a', 'en', 'de', 'por'],
                correct: 0,
            },
            {
                id: 'es_a2_10',
                question: 'What does "Todavía no he terminado" mean?',
                options: ['I have already finished', 'I never finished', 'I haven\'t finished yet', 'I will finish soon'],
                correct: 2,
            },
        ],
        B1: [
            {
                id: 'es_b1_1',
                question: 'Choose the correct form: "Espero que él ___ pronto."',
                options: ['viene', 'venga', 'viniera', 'ha venido'],
                correct: 1,
            },
            {
                id: 'es_b1_2',
                question: 'Complete: "Si tuviera más tiempo, ___ al gimnasio."',
                options: ['voy', 'iría', 'iba', 'he ido'],
                correct: 1,
            },
            {
                id: 'es_b1_3',
                question: 'Which sentence correctly uses the subjunctive?',
                options: [
                    'Creo que Marco tiene razón.',
                    'Pienso que Marco tenga razón.',
                    'No creo que Marco tenga razón.',
                    'Sé que Marco tenga razón.',
                ],
                correct: 2,
            },
            {
                id: 'es_b1_4',
                question: 'What does "aunque" mean?',
                options: ['therefore', 'however', 'although', 'unless'],
                correct: 2,
            },
            {
                id: 'es_b1_5',
                question: 'Complete: "El tren ___ ya cuando llegamos a la estación."',
                options: ['ha salido', 'salía', 'había salido', 'saldrá'],
                correct: 2,
            },
            {
                id: 'es_b1_6',
                question: 'Which sentence correctly uses an indirect object pronoun?',
                options: [
                    'Lo escribí una carta.',
                    'Le escribí una carta.',
                    'Lo escribí a él una carta.',
                    'Ha escrito le una carta.',
                ],
                correct: 1,
            },
            {
                id: 'es_b1_7',
                question: 'What does "a menos que" mean?',
                options: ['as soon as', 'unless', 'because', 'even though'],
                correct: 1,
            },
            {
                id: 'es_b1_8',
                question: 'Complete: "Mientras ___ la cena, sonó el teléfono."',
                options: ['preparo', 'he preparado', 'preparaba', 'prepararé'],
                correct: 2,
            },
            {
                id: 'es_b1_9',
                question: 'Which sentence best expresses "I would have liked to go"?',
                options: [
                    'Quisiera ir.',
                    'Habría ido.',
                    'Habría querido ir.',
                    'Quería ir.',
                ],
                correct: 2,
            },
            {
                id: 'es_b1_10',
                question: 'What does "en cuanto" mean in: "En cuanto llegue, te llamo"?',
                options: ['maybe', 'as soon as', 'usually', 'almost'],
                correct: 1,
            },
        ],
        B2: [
            {
                id: 'es_b2_1',
                question: 'Which sentence uses the subjunctive correctly after "aunque"?',
                options: [
                    'Aunque estoy cansado, sigo trabajando.',
                    'Aunque estaba cansado, sigo trabajando.',
                    'Aunque esté cansado, sigo trabajando.',
                    'Aunque estaré cansado, sigo trabajando.',
                ],
                correct: 2,
            },
            {
                id: 'es_b2_2',
                question: 'What does "a no ser que" mean?',
                options: ['as long as', 'unless', 'although', 'provided that'],
                correct: 1,
            },
            {
                id: 'es_b2_3',
                question: 'Choose the correct passive construction: "El informe ___ por el director ayer."',
                options: [
                    'fue firmado',
                    'ha firmado',
                    'está firmando',
                    'se ha firmado',
                ],
                correct: 0,
            },
            {
                id: 'es_b2_4',
                question: 'Complete: "Si hubiera salido antes, ___ a tiempo."',
                options: [
                    'habría llegado',
                    'llegaría',
                    'he llegado',
                    'llegara',
                ],
                correct: 0,
            },
            {
                id: 'es_b2_5',
                question: 'Which word best replaces "muy grande" in formal writing?',
                options: ['grandote', 'enorme', 'tan grande', 'más grande'],
                correct: 1,
            },
            {
                id: 'es_b2_6',
                question: 'Which sentence correctly uses "para que"?',
                options: [
                    'Estudio para que paso el examen.',
                    'Estudio para que pase el examen.',
                    'Estudio para que he pasado el examen.',
                    'Estudio para que pasaré el examen.',
                ],
                correct: 1,
            },
            {
                id: 'es_b2_7',
                question: 'What nuance does "sino que" add compared to "pero"?',
                options: [
                    'It means "and" instead of "but".',
                    'It introduces a stronger contrast, correcting a preceding negative — "but rather".',
                    'It is used only in questions.',
                    'It softens the contrast, meaning "perhaps".',
                ],
                correct: 1,
            },
            {
                id: 'es_b2_8',
                question: 'Complete: "Cualquier cosa que ___ , te apoyaré."',
                options: ['decides', 'decidas', 'decidirás', 'has decidido'],
                correct: 1,
            },
            {
                id: 'es_b2_9',
                question: 'Which sentence is grammatically correct in formal register?',
                options: [
                    'Le dije que viniera conmigo.',
                    'Lo dije que viniera conmigo.',
                    'Le dije que venga conmigo, refiriéndose al pasado.',
                    'Se lo dije a él que viniera conmigo.',
                ],
                correct: 0,
            },
            {
                id: 'es_b2_10',
                question: 'What does "a pesar de" mean?',
                options: ['because of', 'despite', 'thanks to', 'in order to'],
                correct: 1,
            },
        ],
        C1: [
            {
                id: 'es_c1_1',
                question: 'Which sentence is most appropriate for formal academic writing?',
                options: [
                    'Le dije que no iba allí.',
                    'Dije a él que no iba allí.',
                    'Le comuniqué que no me presentaría en dicho lugar.',
                    'No quería ir y se lo hice saber.',
                ],
                correct: 2,
            },
            {
                id: 'es_c1_2',
                question: 'What is the function of the "pluscuamperfecto de subjuntivo"?',
                options: [
                    'To express future hypotheticals',
                    'To express unrealised past conditions in contrary-to-fact sentences',
                    'To express polite requests in the present',
                    'To express wishes about the future',
                ],
                correct: 1,
            },
            {
                id: 'es_c1_3',
                question: 'Complete: "___ hubiera estudiado más, habría aprobado el examen."',
                options: ['Si', 'Cuando', 'Con tal de que', 'A menos que'],
                correct: 0,
            },
            {
                id: 'es_c1_4',
                question: 'Which is an example of the "condicional compuesto" (impossible past)?',
                options: [
                    'Si estudio, apruebo el examen.',
                    'Si estudiara, aprobaría el examen.',
                    'Si hubiera estudiado, habría aprobado el examen.',
                    'Cuando estudio, apruebo el examen.',
                ],
                correct: 2,
            },
            {
                id: 'es_c1_5',
                question: 'What nuance does "sin embargo" add to a sentence?',
                options: [
                    'It introduces a consequence.',
                    'It introduces a surprising contrast — "however" or "nevertheless".',
                    'It introduces a condition.',
                    'It introduces an alternative.',
                ],
                correct: 1,
            },
            {
                id: 'es_c1_6',
                question: 'Which sentence correctly uses "siempre que"?',
                options: [
                    'Siempre que llueve, me quedo en casa.',
                    'Siempre que lloviera, me quedaría en casa.',
                    'Siempre que ha llovido, me he quedado en casa.',
                    'Siempre que lloverá, me quedaré en casa.',
                ],
                correct: 1,
            },
            {
                id: 'es_c1_7',
                question: 'What does "no es que" typically signal at the start of a sentence?',
                options: [
                    'A strong affirmation',
                    'A polite hedge before a mild denial or clarification',
                    'A direct command',
                    'A future prediction',
                ],
                correct: 1,
            },
            {
                id: 'es_c1_8',
                question: 'Which construction correctly expresses "the more..., the more..."?',
                options: [
                    'Cuanto más..., más...',
                    'Tanto... como...',
                    'Tan pronto... como...',
                    'Ni... ni...',
                ],
                correct: 0,
            },
            {
                id: 'es_c1_9',
                question: 'What is the effect of using "quienquiera" with the subjunctive, as in "Quienquiera que seas..."?',
                options: [
                    'It expresses certainty about someone\'s identity.',
                    'It expresses indifference to who the person might be — "whoever you are".',
                    'It is a polite greeting.',
                    'It is only used with animals.',
                ],
                correct: 1,
            },
            {
                id: 'es_c1_10',
                question: 'Which sentence demonstrates correct use of the "gerundio compuesto"?',
                options: [
                    'Habiendo terminado el trabajo, salí.',
                    'Terminando el trabajo, salí.',
                    'Haber terminado el trabajo, salí.',
                    'Terminado el trabajo, habiendo salido.',
                ],
                correct: 0,
            },
        ],
        C2: [
            {
                id: 'es_c2_1',
                question: 'Which word best completes: "El silencio era ___, casi capaz de sofocar cada pensamiento."',
                options: ['grande', 'ensordecedor', 'tranquilo', 'largo'],
                correct: 1,
            },
            {
                id: 'es_c2_2',
                question: 'What stylistic effect does "pretérito" vs "presente perfecto" create in formal writing?',
                options: [
                    'No difference — they are interchangeable.',
                    'It creates psychological distance, giving events a more literary or historical tone.',
                    'It is more informal and conversational.',
                    'It is only used in questions.',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_3',
                question: 'Which sentence demonstrates the most sophisticated use of the gerundio?',
                options: [
                    'Comiendo, veo la televisión.',
                    'Habiendo ya leído la novela, pude analizar sus estructuras narrativas con mayor profundidad.',
                    'Corriendo, llegué tarde.',
                    'Estudiando mucho, aprendí español.',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_4',
                question: 'What does "no ver la hora" mean?',
                options: [
                    'To not have enough time',
                    'To be unable to read a clock',
                    'To look forward to something eagerly',
                    'To be running late',
                ],
                correct: 2,
            },
            {
                id: 'es_c2_5',
                question: 'In which context would you use "se desprende que" in formal Spanish?',
                options: [
                    'When giving a personal opinion',
                    'When drawing a logical conclusion from evidence — "it follows that"',
                    'When expressing doubt',
                    'When making a polite request',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_6',
                question: 'What does the construction "no es que no... sino que" convey?',
                options: [
                    'A flat denial',
                    'A rhetorical concession before qualifying a statement — "it\'s not that... but rather"',
                    'A simple negative statement',
                    'A question',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_7',
                question: 'Which sentence best reflects sophisticated literary register?',
                options: [
                    'Caminaba despacio porque estaba cansado.',
                    'Caminaba con paso lento, como queriendo prolongar el instante.',
                    'Caminaba lentamente porque cansado.',
                    'Estaba cansado y por eso caminaba despacio.',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_8',
                question: 'What is the function of "en tanto que" in formal writing?',
                options: [
                    'It means "wherever" in a literal spatial sense only.',
                    'It can introduce a contrast or simultaneity, meaning "whereas" or "while".',
                    'It is used only for physical locations.',
                    'It is an informal filler word.',
                ],
                correct: 1,
            },
            {
                id: 'es_c2_9',
                question: 'Which idiom best conveys "to read between the lines"?',
                options: [
                    'Leer entre líneas',
                    'Leer en voz alta',
                    'Echar un vistazo',
                    'Tomar al pie de la letra',
                ],
                correct: 0,
            },
            {
                id: 'es_c2_10',
                question: 'What subtle distinction does "sino" carry that "pero" does not, in careful writing?',
                options: [
                    'No distinction — fully interchangeable',
                    'It replaces a preceding negative with a corrective alternative — "but rather"',
                    'It is used only at the start of a paragraph',
                    'It signals agreement rather than contrast',
                ],
                correct: 1,
            },
        ],
    },
    french: {
        A1: [
            {
                id: 'fr_a1_1',
                question: 'Which word means "good morning" in French?',
                options: ['Bonsoir', 'Bonne nuit', 'Bonjour', 'Au revoir'],
                correct: 2,
            },
            {
                id: 'fr_a1_2',
                question: 'Complete: "Je ___ étudiant."',
                options: ['es', 'suis', 'est', 'sommes'],
                correct: 1,
            },
            {
                id: 'fr_a1_3',
                question: 'What does "Où habites-tu?" mean?',
                options: ['What do you do?', 'How old are you?', 'Where do you live?', 'What is your name?'],
                correct: 2,
            },
            {
                id: 'fr_a1_4',
                question: 'Which is the correct plural of "le livre"?',
                options: ['le livres', 'la livre', 'les livres', 'les livre'],
                correct: 2,
            },
            {
                id: 'fr_a1_5',
                question: 'What does "J\'ai faim" mean?',
                options: ['I am thirsty', 'I am hungry', 'I am tired', 'I am cold'],
                correct: 1,
            },
            {
                id: 'fr_a1_6',
                question: 'Which word means "thank you"?',
                options: ['De rien', 'Merci', 'Pardon', 'S\'il vous plaît'],
                correct: 1,
            },
            {
                id: 'fr_a1_7',
                question: 'Complete: "Elle ___ française."',
                options: ['suis', 'es', 'est', 'sommes'],
                correct: 2,
            },
            {
                id: 'fr_a1_8',
                question: 'What is the correct article for "maison" (house)?',
                options: ['le', 'la', 'les', 'un'],
                correct: 1,
            },
            {
                id: 'fr_a1_9',
                question: 'What does "Quel âge as-tu?" mean?',
                options: ['What time is it?', 'How old are you?', 'How are you?', 'Where are you from?'],
                correct: 1,
            },
            {
                id: 'fr_a1_10',
                question: 'Which sentence correctly says "I have two brothers"?',
                options: ['J\'ai deux frère', 'J\'ai deux frères', 'Je suis deux frères', 'J\'ai deux sœurs'],
                correct: 1,
            },
        ],
        A2: [
            {
                id: 'fr_a2_1',
                question: 'Which sentence correctly uses the passé composé?',
                options: [
                    'Hier je mange de la pizza.',
                    'Hier j\'ai mangé de la pizza.',
                    'Hier je mangeais de la pizza.',
                    'Hier je mangerai de la pizza.',
                ],
                correct: 1,
            },
            {
                id: 'fr_a2_2',
                question: 'What does "J\'aime les pommes" mean?',
                options: ['I want apples', 'I have apples', 'I like apples', 'I need apples'],
                correct: 2,
            },
            {
                id: 'fr_a2_3',
                question: 'Complete: "Chaque jour je ___ au travail à vélo."',
                options: ['suis allé', 'allais', 'vais', 'irai'],
                correct: 2,
            },
            {
                id: 'fr_a2_4',
                question: 'Which sentence uses the imparfait correctly?',
                options: [
                    'Enfant, j\'ai joué dehors tous les jours.',
                    'Enfant, je jouais dehors tous les jours.',
                    'Enfant, je joue dehors tous les jours.',
                    'Enfant, je jouerai dehors tous les jours.',
                ],
                correct: 1,
            },
            {
                id: 'fr_a2_5',
                question: 'What is the correct form of "aller" for "vous"?',
                options: ['allez', 'vont', 'allons', 'vas'],
                correct: 0,
            },
            {
                id: 'fr_a2_6',
                question: 'Complete: "Demain je ___ à Paris."',
                options: ['vais', 'allais', 'suis allé', 'irai'],
                correct: 3,
            },
            {
                id: 'fr_a2_7',
                question: 'What does "Je dois étudier ce soir" mean?',
                options: ['I studied last night', 'I have to study tonight', 'I want to study tonight', 'I was studying tonight'],
                correct: 1,
            },
            {
                id: 'fr_a2_8',
                question: 'Which is the correct comparative: "Marc est ___ grand que moi."',
                options: ['très', 'plus', 'aussi', 'moins de'],
                correct: 1,
            },
            {
                id: 'fr_a2_9',
                question: 'Complete: "Elle se réveille ___ sept heures chaque matin."',
                options: ['à', 'en', 'de', 'pour'],
                correct: 0,
            },
            {
                id: 'fr_a2_10',
                question: 'What does "Je n\'ai pas encore fini" mean?',
                options: ['I have already finished', 'I never finished', 'I haven\'t finished yet', 'I will finish soon'],
                correct: 2,
            },
        ],
        B1: [
            {
                id: 'fr_b1_1',
                question: 'Choose the correct form: "J\'espère qu\'il ___ bientôt."',
                options: ['vient', 'vienne', 'venait', 'est venu'],
                correct: 1,
            },
            {
                id: 'fr_b1_2',
                question: 'Complete: "Si j\'avais plus de temps, ___ à la salle de sport."',
                options: ['je vais', 'j\'irais', 'j\'allais', 'je suis allé'],
                correct: 1,
            },
            {
                id: 'fr_b1_3',
                question: 'Which sentence correctly uses the subjonctif?',
                options: [
                    'Je pense que Marc a raison.',
                    'Je pense que Marc ait raison.',
                    'Je ne pense pas que Marc ait raison.',
                    'Je sais que Marc ait raison.',
                ],
                correct: 2,
            },
            {
                id: 'fr_b1_4',
                question: 'What does "bien que" mean?',
                options: ['therefore', 'however', 'although', 'unless'],
                correct: 2,
            },
            {
                id: 'fr_b1_5',
                question: 'Complete: "Le train ___ déjà parti quand nous sommes arrivés à la gare."',
                options: ['est', 'était', 'sera', 'a été'],
                correct: 1,
            },
            {
                id: 'fr_b1_6',
                question: 'Which sentence correctly uses an indirect object pronoun?',
                options: [
                    'Je l\'ai écrit une lettre.',
                    'Je lui ai écrit une lettre.',
                    'Je l\'ai écrit à lui une lettre.',
                    'A écrit lui une lettre.',
                ],
                correct: 1,
            },
            {
                id: 'fr_b1_7',
                question: 'What does "à moins que" mean?',
                options: ['as soon as', 'unless', 'because', 'even though'],
                correct: 1,
            },
            {
                id: 'fr_b1_8',
                question: 'Complete: "Pendant que je ___ le dîner, le téléphone a sonné."',
                options: ['prépare', 'ai préparé', 'préparais', 'préparerai'],
                correct: 2,
            },
            {
                id: 'fr_b1_9',
                question: 'Which sentence best expresses "I would have liked to go"?',
                options: [
                    'Je voudrais y aller.',
                    'Je serais allé.',
                    'J\'aurais voulu y aller.',
                    'Je voulais y aller.',
                ],
                correct: 2,
            },
            {
                id: 'fr_b1_10',
                question: 'What does "dès que" mean in: "Dès que j\'arrive, je t\'appelle"?',
                options: ['maybe', 'as soon as', 'usually', 'almost'],
                correct: 1,
            },
        ],
        B2: [
            {
                id: 'fr_b2_1',
                question: 'Which sentence uses the subjonctif correctly after "bien que"?',
                options: [
                    'Bien que je suis fatigué, je continue à travailler.',
                    'Bien que j\'étais fatigué, je continue à travailler.',
                    'Bien que je sois fatigué, je continue à travailler.',
                    'Bien que je serai fatigué, je continue à travailler.',
                ],
                correct: 2,
            },
            {
                id: 'fr_b2_2',
                question: 'What does "à moins que ne" mean?',
                options: ['as long as', 'unless', 'although', 'provided that'],
                correct: 1,
            },
            {
                id: 'fr_b2_3',
                question: 'Choose the correct passive construction: "Le rapport ___ par le directeur hier."',
                options: [
                    'a été signé',
                    'a signé',
                    'est en train de signer',
                    's\'est signé',
                ],
                correct: 0,
            },
            {
                id: 'fr_b2_4',
                question: 'Complete: "Si j\'étais parti plus tôt, ___ à l\'heure."',
                options: [
                    'je serais arrivé',
                    'j\'arriverais',
                    'je suis arrivé',
                    'j\'arrivasse',
                ],
                correct: 0,
            },
            {
                id: 'fr_b2_5',
                question: 'Which word best replaces "très grand" in formal writing?',
                options: ['grandiose', 'immense', 'si grand', 'plus grand'],
                correct: 1,
            },
            {
                id: 'fr_b2_6',
                question: 'Which sentence correctly uses "afin que"?',
                options: [
                    'J\'étudie afin que je passe l\'examen.',
                    'J\'étudie afin que je passe l\'examen.',
                    'J\'étudie afin que j\'ai passé l\'examen.',
                    'J\'étudie afin que je passerai l\'examen.',
                ],
                correct: 0,
            },
            {
                id: 'fr_b2_7',
                question: 'What nuance does "mais plutôt" add compared to "mais"?',
                options: [
                    'It means "and" instead of "but".',
                    'It introduces a stronger contrast, correcting a preceding negative — "but rather".',
                    'It is used only in questions.',
                    'It softens the contrast, meaning "perhaps".',
                ],
                correct: 1,
            },
            {
                id: 'fr_b2_8',
                question: 'Complete: "Quoi que tu ___ , je te soutiendrai."',
                options: ['décides', 'décide', 'décideras', 'as décidé'],
                correct: 1,
            },
            {
                id: 'fr_b2_9',
                question: 'Which sentence is grammatically correct in formal register?',
                options: [
                    'Je lui ai dit de venir avec moi.',
                    'Je l\'ai dit de venir avec moi.',
                    'Je lui ai dit qu\'il vienne avec moi, au passé.',
                    'Je le lui ai dit à lui de venir avec moi.',
                ],
                correct: 0,
            },
            {
                id: 'fr_b2_10',
                question: 'What does "malgré" mean?',
                options: ['because of', 'despite', 'thanks to', 'in order to'],
                correct: 1,
            },
        ],
        C1: [
            {
                id: 'fr_c1_1',
                question: 'Which sentence is most appropriate for formal academic writing?',
                options: [
                    'Je lui ai dit que je n\'y allais pas.',
                    'J\'ai dit à lui que je n\'allais pas là.',
                    'Je lui ai communiqué que je ne me rendrais pas en ce lieu.',
                    'Je ne voulais pas y aller et je le lui ai fait savoir.',
                ],
                correct: 2,
            },
            {
                id: 'fr_c1_2',
                question: 'What is the function of the "plus-que-parfait du subjonctif"?',
                options: [
                    'To express future hypotheticals',
                    'To express unrealised past conditions in very formal/literary contrary-to-fact sentences',
                    'To express polite requests in the present',
                    'To express wishes about the future',
                ],
                correct: 1,
            },
            {
                id: 'fr_c1_3',
                question: 'Complete: "___ j\'avais étudié davantage, j\'aurais réussi l\'examen."',
                options: ['Si', 'Quand', 'Pourvu que', 'À moins que'],
                correct: 0,
            },
            {
                id: 'fr_c1_4',
                question: 'Which is an example of the impossible past conditional?',
                options: [
                    'Si j\'étudie, je réussis l\'examen.',
                    'Si j\'étudiais, je réussirais l\'examen.',
                    'Si j\'avais étudié, j\'aurais réussi l\'examen.',
                    'Quand j\'étudie, je réussis l\'examen.',
                ],
                correct: 2,
            },
            {
                id: 'fr_c1_5',
                question: 'What nuance does "et pourtant" add to a sentence?',
                options: [
                    'It introduces a consequence.',
                    'It introduces a surprising contrast — "and yet" or "even so".',
                    'It introduces a condition.',
                    'It introduces an alternative.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c1_6',
                question: 'Which sentence correctly uses "pourvu que"?',
                options: [
                    'Pourvu qu\'il pleut, je reste à la maison.',
                    'Pourvu qu\'il pleuve, je resterais à la maison.',
                    'Pourvu qu\'il a plu, je suis resté à la maison.',
                    'Pourvu qu\'il pleuvra, je resterai à la maison.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c1_7',
                question: 'What does "ce n\'est pas que" typically signal at the start of a sentence?',
                options: [
                    'A strong affirmation',
                    'A polite hedge before a mild denial or clarification',
                    'A direct command',
                    'A future prediction',
                ],
                correct: 1,
            },
            {
                id: 'fr_c1_8',
                question: 'Which construction correctly expresses "the more..., the more..."?',
                options: [
                    'Plus..., plus...',
                    'Autant..., autant...',
                    'Soit..., soit...',
                    'Ni..., ni...',
                ],
                correct: 0,
            },
            {
                id: 'fr_c1_9',
                question: 'What is the effect of using "qui que" with the subjunctive, as in "Qui que tu sois..."?',
                options: [
                    'It expresses certainty about someone\'s identity.',
                    'It expresses indifference to who the person might be — "whoever you are".',
                    'It is a polite greeting.',
                    'It is only used with animals.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c1_10',
                question: 'Which sentence demonstrates correct use of the "participe passé composé"?',
                options: [
                    'Ayant fini le travail, je suis sorti.',
                    'Finissant le travail, je suis sorti.',
                    'Avoir fini le travail, je suis sorti.',
                    'Fini le travail, étant sorti.',
                ],
                correct: 0,
            },
        ],
        C2: [
            {
                id: 'fr_c2_1',
                question: 'Which word best completes: "Le silence était ___, presque capable d\'étouffer chaque pensée."',
                options: ['grand', 'assourdissant', 'calme', 'long'],
                correct: 1,
            },
            {
                id: 'fr_c2_2',
                question: 'What stylistic effect does the "passé simple" create vs "passé composé" in formal writing?',
                options: [
                    'No difference — they are interchangeable.',
                    'It creates psychological distance, giving events a more literary or historical tone.',
                    'It is more informal and conversational.',
                    'It is only used in questions.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_3',
                question: 'Which sentence demonstrates the most sophisticated use of the gérondif?',
                options: [
                    'En mangeant, je regarde la télévision.',
                    'Ayant déjà lu le roman, j\'ai pu en analyser les structures narratives avec plus de profondeur.',
                    'En courant, je suis arrivé en retard.',
                    'En étudiant beaucoup, j\'ai appris le français.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_4',
                question: 'What does "avoir hâte de" mean?',
                options: [
                    'To not have enough time',
                    'To be unable to read a clock',
                    'To look forward to something eagerly',
                    'To be running late',
                ],
                correct: 2,
            },
            {
                id: 'fr_c2_5',
                question: 'In which context would you use "il en ressort que" in formal French?',
                options: [
                    'When giving a personal opinion',
                    'When drawing a logical conclusion from evidence — "it emerges that"',
                    'When expressing doubt',
                    'When making a polite request',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_6',
                question: 'What does the construction "ce n\'est pas que... mais plutôt" convey?',
                options: [
                    'A flat denial',
                    'A rhetorical concession before qualifying a statement — "it\'s not that... but rather"',
                    'A simple negative statement',
                    'A question',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_7',
                question: 'Which sentence best reflects sophisticated literary register?',
                options: [
                    'Il marchait lentement parce qu\'il était fatigué.',
                    'Il marchait d\'un pas lent, comme pour prolonger l\'instant.',
                    'Il marchait lentement car fatigué.',
                    'Il était fatigué et donc marchait lentement.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_8',
                question: 'What is the function of "moyennant quoi" in formal writing?',
                options: [
                    'It means "wherever" in a literal spatial sense only.',
                    'It introduces a consequence achieved through the means just mentioned — "in return for which".',
                    'It is used only for physical locations.',
                    'It is an informal filler word.',
                ],
                correct: 1,
            },
            {
                id: 'fr_c2_9',
                question: 'Which idiom best conveys "to read between the lines"?',
                options: [
                    'Lire entre les lignes',
                    'Lire à voix haute',
                    'Jeter un coup d\'œil',
                    'Prendre au pied de la lettre',
                ],
                correct: 0,
            },
            {
                id: 'fr_c2_10',
                question: 'What subtle distinction does "quoique" carry compared to "mais", in careful writing?',
                options: [
                    'No distinction — fully interchangeable',
                    'It concedes a point with a subjunctive clause, similar to "although" but more literary',
                    'It is used only at the start of a paragraph',
                    'It signals agreement rather than contrast',
                ],
                correct: 1,
            },
        ],
    },
    portuguese: {
        A1: [
            {
                id: 'pt_a1_1',
                question: 'Which word means "good morning" in Portuguese?',
                options: ['Boa noite', 'Boa tarde', 'Bom dia', 'Adeus'],
                correct: 2,
            },
            {
                id: 'pt_a1_2',
                question: 'Complete: "Eu ___ estudante."',
                options: ['és', 'sou', 'é', 'somos'],
                correct: 1,
            },
            {
                id: 'pt_a1_3',
                question: 'What does "Onde você mora?" mean?',
                options: ['What do you do?', 'How old are you?', 'Where do you live?', 'What is your name?'],
                correct: 2,
            },
            {
                id: 'pt_a1_4',
                question: 'Which is the correct plural of "o livro"?',
                options: ['o livros', 'a livro', 'os livros', 'as livros'],
                correct: 2,
            },
            {
                id: 'pt_a1_5',
                question: 'What does "Estou com fome" mean?',
                options: ['I am thirsty', 'I am hungry', 'I am tired', 'I am cold'],
                correct: 1,
            },
            {
                id: 'pt_a1_6',
                question: 'Which word means "thank you"?',
                options: ['De nada', 'Obrigado', 'Desculpe', 'Por favor'],
                correct: 1,
            },
            {
                id: 'pt_a1_7',
                question: 'Complete: "Ela ___ brasileira."',
                options: ['sou', 'és', 'é', 'somos'],
                correct: 2,
            },
            {
                id: 'pt_a1_8',
                question: 'What is the correct article for "casa" (house)?',
                options: ['o', 'a', 'os', 'um'],
                correct: 1,
            },
            {
                id: 'pt_a1_9',
                question: 'What does "Quantos anos você tem?" mean?',
                options: ['What time is it?', 'How old are you?', 'How are you?', 'Where are you from?'],
                correct: 1,
            },
            {
                id: 'pt_a1_10',
                question: 'Which sentence correctly says "I have two brothers"?',
                options: ['Tenho dois irmão', 'Tenho dois irmãos', 'Sou dois irmãos', 'Tenho dois irmãs'],
                correct: 1,
            },
        ],
        A2: [
            {
                id: 'pt_a2_1',
                question: 'Which sentence correctly uses the pretérito perfeito?',
                options: [
                    'Ontem eu como pizza.',
                    'Ontem eu comi pizza.',
                    'Ontem eu comia pizza.',
                    'Ontem eu comerei pizza.',
                ],
                correct: 1,
            },
            {
                id: 'pt_a2_2',
                question: 'What does "Eu gosto de maçãs" mean?',
                options: ['I want apples', 'I have apples', 'I like apples', 'I need apples'],
                correct: 2,
            },
            {
                id: 'pt_a2_3',
                question: 'Complete: "Todos os dias eu ___ ao trabalho de bicicleta."',
                options: ['fui', 'ia', 'vou', 'irei'],
                correct: 2,
            },
            {
                id: 'pt_a2_4',
                question: 'Which sentence uses the imperfeito correctly?',
                options: [
                    'Quando criança, brinquei fora todos os dias.',
                    'Quando criança, brincava fora todos os dias.',
                    'Quando criança, brinco fora todos os dias.',
                    'Quando criança, brincarei fora todos os dias.',
                ],
                correct: 1,
            },
            {
                id: 'pt_a2_5',
                question: 'What is the correct form of "ir" for "nós"?',
                options: ['vamos', 'vão', 'vai', 'vais'],
                correct: 0,
            },
            {
                id: 'pt_a2_6',
                question: 'Complete: "Amanhã eu ___ para Lisboa."',
                options: ['vou', 'ia', 'fui', 'irei'],
                correct: 3,
            },
            {
                id: 'pt_a2_7',
                question: 'What does "Eu tenho que estudar hoje à noite" mean?',
                options: ['I studied last night', 'I have to study tonight', 'I want to study tonight', 'I was studying tonight'],
                correct: 1,
            },
            {
                id: 'pt_a2_8',
                question: 'Which is the correct comparative: "Marco é ___ alto do que eu."',
                options: ['muito', 'mais', 'tão', 'menos de'],
                correct: 1,
            },
            {
                id: 'pt_a2_9',
                question: 'Complete: "Ela acorda ___ sete horas todas as manhãs."',
                options: ['às', 'em', 'de', 'para'],
                correct: 0,
            },
            {
                id: 'pt_a2_10',
                question: 'What does "Ainda não terminei" mean?',
                options: ['I have already finished', 'I never finished', 'I haven\'t finished yet', 'I will finish soon'],
                correct: 2,
            },
        ],
        B1: [
            {
                id: 'pt_b1_1',
                question: 'Choose the correct form: "Espero que ele ___ logo."',
                options: ['vem', 'venha', 'vinha', 'veio'],
                correct: 1,
            },
            {
                id: 'pt_b1_2',
                question: 'Complete: "Se eu tivesse mais tempo, ___ à academia."',
                options: ['vou', 'iria', 'ia', 'fui'],
                correct: 1,
            },
            {
                id: 'pt_b1_3',
                question: 'Which sentence correctly uses the subjuntivo?',
                options: [
                    'Acho que o Marco tem razão.',
                    'Penso que o Marco tenha razão.',
                    'Não acho que o Marco tenha razão.',
                    'Sei que o Marco tenha razão.',
                ],
                correct: 2,
            },
            {
                id: 'pt_b1_4',
                question: 'What does "embora" mean?',
                options: ['therefore', 'however', 'although', 'unless'],
                correct: 2,
            },
            {
                id: 'pt_b1_5',
                question: 'Complete: "O trem já ___ partido quando chegamos à estação."',
                options: ['tinha', 'tem', 'terá', 'era'],
                correct: 0,
            },
            {
                id: 'pt_b1_6',
                question: 'Which sentence correctly uses an indirect object pronoun?',
                options: [
                    'Eu o escrevi uma carta.',
                    'Eu lhe escrevi uma carta.',
                    'Eu o escrevi a ele uma carta.',
                    'Escreveu lhe uma carta.',
                ],
                correct: 1,
            },
            {
                id: 'pt_b1_7',
                question: 'What does "a menos que" mean?',
                options: ['as soon as', 'unless', 'because', 'even though'],
                correct: 1,
            },
            {
                id: 'pt_b1_8',
                question: 'Complete: "Enquanto eu ___ o jantar, o telefone tocou."',
                options: ['preparo', 'tenho preparado', 'preparava', 'preparerei'],
                correct: 2,
            },
            {
                id: 'pt_b1_9',
                question: 'Which sentence best expresses "I would have liked to go"?',
                options: [
                    'Eu gostaria de ir.',
                    'Eu teria ido.',
                    'Eu teria gostado de ir.',
                    'Eu queria ir.',
                ],
                correct: 2,
            },
            {
                id: 'pt_b1_10',
                question: 'What does "assim que" mean in: "Assim que eu chegar, te ligo"?',
                options: ['maybe', 'as soon as', 'usually', 'almost'],
                correct: 1,
            },
        ],
        B2: [
            {
                id: 'pt_b2_1',
                question: 'Which sentence uses the subjuntivo correctly after "embora"?',
                options: [
                    'Embora eu estou cansado, continuo trabalhando.',
                    'Embora eu estava cansado, continuo trabalhando.',
                    'Embora eu esteja cansado, continuo trabalhando.',
                    'Embora eu estarei cansado, continuo trabalhando.',
                ],
                correct: 2,
            },
            {
                id: 'pt_b2_2',
                question: 'What does "a não ser que" mean?',
                options: ['as long as', 'unless', 'although', 'provided that'],
                correct: 1,
            },
            {
                id: 'pt_b2_3',
                question: 'Choose the correct passive construction: "O relatório ___ pelo diretor ontem."',
                options: [
                    'foi assinado',
                    'tem assinado',
                    'está assinando',
                    'se assinou',
                ],
                correct: 0,
            },
            {
                id: 'pt_b2_4',
                question: 'Complete: "Se eu tivesse saído mais cedo, ___ a tempo."',
                options: [
                    'teria chegado',
                    'chegaria',
                    'tenho chegado',
                    'chegasse',
                ],
                correct: 0,
            },
            {
                id: 'pt_b2_5',
                question: 'Which word best replaces "muito grande" in formal writing?',
                options: ['grandão', 'enorme', 'tão grande', 'maior'],
                correct: 1,
            },
            {
                id: 'pt_b2_6',
                question: 'Which sentence correctly uses "para que"?',
                options: [
                    'Estudo para que passo no exame.',
                    'Estudo para que passe no exame.',
                    'Estudo para que tenho passado no exame.',
                    'Estudo para que passarei no exame.',
                ],
                correct: 1,
            },
            {
                id: 'pt_b2_7',
                question: 'What nuance does "mas sim" add compared to "mas"?',
                options: [
                    'It means "and" instead of "but".',
                    'It introduces a stronger contrast, correcting a preceding negative — "but rather".',
                    'It is used only in questions.',
                    'It softens the contrast, meaning "perhaps".',
                ],
                correct: 1,
            },
            {
                id: 'pt_b2_8',
                question: 'Complete: "Seja o que ___ , eu vou te apoiar."',
                options: ['decides', 'decida', 'decidirás', 'tens decidido'],
                correct: 1,
            },
            {
                id: 'pt_b2_9',
                question: 'Which sentence is grammatically correct in formal register?',
                options: [
                    'Eu disse a ele para vir comigo.',
                    'Eu o disse para vir comigo.',
                    'Eu disse a ele que viesse comigo, no passado.',
                    'Eu lhe disse a ele para vir comigo.',
                ],
                correct: 0,
            },
            {
                id: 'pt_b2_10',
                question: 'What does "apesar de" mean?',
                options: ['because of', 'despite', 'thanks to', 'in order to'],
                correct: 1,
            },
        ],
        C1: [
            {
                id: 'pt_c1_1',
                question: 'Which sentence is most appropriate for formal academic writing?',
                options: [
                    'Eu disse a ele que não ia lá.',
                    'Eu disse a ele que não fui lá.',
                    'Eu lhe comuniquei que não me apresentaria naquele local.',
                    'Eu não queria ir e eu deixei ele saber.',
                ],
                correct: 2,
            },
            {
                id: 'pt_c1_2',
                question: 'What is the function of the "mais-que-perfeito do subjuntivo"?',
                options: [
                    'To express future hypotheticals',
                    'To express unrealised past conditions in formal/literary contrary-to-fact sentences',
                    'To express polite requests in the present',
                    'To express wishes about the future',
                ],
                correct: 1,
            },
            {
                id: 'pt_c1_3',
                question: 'Complete: "___ eu tivesse estudado mais, teria passado no exame."',
                options: ['Se', 'Quando', 'Contanto que', 'A menos que'],
                correct: 0,
            },
            {
                id: 'pt_c1_4',
                question: 'Which is an example of the impossible past conditional?',
                options: [
                    'Se eu estudo, passo no exame.',
                    'Se eu estudasse, passaria no exame.',
                    'Se eu tivesse estudado, teria passado no exame.',
                    'Quando eu estudo, passo no exame.',
                ],
                correct: 2,
            },
            {
                id: 'pt_c1_5',
                question: 'What nuance does "e ainda assim" add to a sentence?',
                options: [
                    'It introduces a consequence.',
                    'It introduces a surprising contrast — "and yet" or "even so".',
                    'It introduces a condition.',
                    'It introduces an alternative.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c1_6',
                question: 'Which sentence correctly uses "contanto que"?',
                options: [
                    'Contanto que chove, fico em casa.',
                    'Contanto que chova, ficarei em casa.',
                    'Contanto que choveu, fiquei em casa.',
                    'Contanto que choverá, ficarei em casa.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c1_7',
                question: 'What does "não é que" typically signal at the start of a sentence?',
                options: [
                    'A strong affirmation',
                    'A polite hedge before a mild denial or clarification',
                    'A direct command',
                    'A future prediction',
                ],
                correct: 1,
            },
            {
                id: 'pt_c1_8',
                question: 'Which construction correctly expresses "the more..., the more..."?',
                options: [
                    'Quanto mais..., mais...',
                    'Tanto... quanto...',
                    'Seja..., seja...',
                    'Nem..., nem...',
                ],
                correct: 0,
            },
            {
                id: 'pt_c1_9',
                question: 'What is the effect of using "quem quer que" with the subjunctive, as in "Quem quer que seja..."?',
                options: [
                    'It expresses certainty about someone\'s identity.',
                    'It expresses indifference to who the person might be — "whoever you are".',
                    'It is a polite greeting.',
                    'It is only used with animals.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c1_10',
                question: 'Which sentence demonstrates correct use of the "gerúndio composto"?',
                options: [
                    'Tendo terminado o trabalho, saí.',
                    'Terminando o trabalho, saí.',
                    'Ter terminado o trabalho, saí.',
                    'Terminado o trabalho, tendo saído.',
                ],
                correct: 0,
            },
        ],
        C2: [
            {
                id: 'pt_c2_1',
                question: 'Which word best completes: "O silêncio era ___, quase capaz de sufocar cada pensamento."',
                options: ['grande', 'ensurdecedor', 'calmo', 'longo'],
                correct: 1,
            },
            {
                id: 'pt_c2_2',
                question: 'What stylistic effect does "pretérito perfeito" vs "pretérito perfeito composto" create in formal writing?',
                options: [
                    'No difference — they are interchangeable.',
                    'The composto form emphasizes a repeated or ongoing action up to the present, unlike the simple past.',
                    'It is more informal and conversational.',
                    'It is only used in questions.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_3',
                question: 'Which sentence demonstrates the most sophisticated use of the gerúndio?',
                options: [
                    'Comendo, assisto à televisão.',
                    'Tendo já lido o romance, pude analisar suas estruturas narrativas com maior profundidade.',
                    'Correndo, cheguei atrasado.',
                    'Estudando muito, aprendi português.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_4',
                question: 'What does "mal posso esperar" mean?',
                options: [
                    'To not have enough time',
                    'To be unable to read a clock',
                    'To look forward to something eagerly',
                    'To be running late',
                ],
                correct: 2,
            },
            {
                id: 'pt_c2_5',
                question: 'In which context would you use "depreende-se que" in formal Portuguese?',
                options: [
                    'When giving a personal opinion',
                    'When drawing a logical conclusion from evidence — "it can be inferred that"',
                    'When expressing doubt',
                    'When making a polite request',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_6',
                question: 'What does the construction "não é que não... mas sim" convey?',
                options: [
                    'A flat denial',
                    'A rhetorical concession before qualifying a statement — "it\'s not that... but rather"',
                    'A simple negative statement',
                    'A question',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_7',
                question: 'Which sentence best reflects sophisticated literary register?',
                options: [
                    'Ele andava devagar porque estava cansado.',
                    'Ele caminhava com passos lentos, como que a querer prolongar o instante.',
                    'Ele andava devagar pois cansado.',
                    'Ele estava cansado e por isso andava devagar.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_8',
                question: 'What is the function of "mediante" in formal writing?',
                options: [
                    'It means "wherever" in a literal spatial sense only.',
                    'It introduces the means or condition by which something happens — "by means of" or "provided that".',
                    'It is used only for physical locations.',
                    'It is an informal filler word.',
                ],
                correct: 1,
            },
            {
                id: 'pt_c2_9',
                question: 'Which idiom best conveys "to read between the lines"?',
                options: [
                    'Ler nas entrelinhas',
                    'Ler em voz alta',
                    'Dar uma olhada',
                    'Levar ao pé da letra',
                ],
                correct: 0,
            },
            {
                id: 'pt_c2_10',
                question: 'What subtle distinction does "senão" carry compared to "mas", in careful writing?',
                options: [
                    'No distinction — fully interchangeable',
                    'It replaces a preceding negative with a corrective alternative — "but rather", or introduces "otherwise"',
                    'It is used only at the start of a paragraph',
                    'It signals agreement rather than contrast',
                ],
                correct: 1,
            },
        ],
    },
};

const LEVEL_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function shuffleArray(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

const languageCodeByName = { english: 'en', italian: 'it', spanish: 'es', french: 'fr', portuguese: 'pt' };

// Questions like "What does X mean?" or "Which sentence says Y?" only work when the
// question language and the answer language are different.
function isTranslationQuestion(q) {
    return /^(What does .+ mean|Which word means|Which sentence (correctly )?(says|expresses)|Which sentence best expresses|Which idiom best conveys|Which construction correctly expresses|What is the correct article for)/i.test(q.question);
}

function buildAdaptiveTest(targetLanguage, interfaceLang) {
    // Start at A2, go up through C2, sampling 5 random questions per level from
    // the larger pool each time so the test isn't memorizable on retakes.
    // A1 only gets shown if the user struggles early (handled by the early-exit
    // logic), never as a "everything else was correct" afterthought.
    // If the interface language is the same as the language being learned,
    // translation-style questions give away the answer, so they are used last.
    const langKey = (targetLanguage || 'italian').toLowerCase();
    const allQuestions = allQuestionsByLanguage[langKey] || allQuestionsByLanguage.italian;
    const sameLanguage = languageCodeByName[langKey] === interfaceLang;
    const pickFive = (pool) => {
        if (!sameLanguage) return shuffleArray(pool).slice(0, 5);
        const good = shuffleArray(pool.filter(q => !isTranslationQuestion(q)));
        const rest = shuffleArray(pool.filter(q => isTranslationQuestion(q)));
        return [...good, ...rest].slice(0, 5);
    };
    const sorted = [];
    const startIdx = LEVEL_ORDER.indexOf('A2');
    for (let i = startIdx; i < LEVEL_ORDER.length; i++) {
        const level = LEVEL_ORDER[i];
        const pool = pickFive(allQuestions[level] || []);
        pool.forEach(q => sorted.push({ ...q, level }));
    }
    const a1Pool = pickFive(allQuestions['A1'] || []);
    a1Pool.forEach(q => sorted.push({ ...q, level: 'A1' }));
    return sorted;
}

function calculateAdaptiveLevel(answers, questions) {
    const levelScores = {};
    const levelTotals = {};
    LEVEL_ORDER.forEach(l => { levelScores[l] = 0; levelTotals[l] = 0; });
    questions.forEach((q, i) => {
        if (answers[i] !== undefined) {
            levelTotals[q.level]++;
            if (answers[i] === q.correct) levelScores[q.level]++;
        }
    });
    const pct = (level) => levelTotals[level] === 0 ? null : levelScores[level] / levelTotals[level];

    const a1 = pct('A1');
    const a2 = pct('A2');
    const b1 = pct('B1');
    const b2 = pct('B2');
    const c1 = pct('C1');
    const c2 = pct('C2');

    // Find the highest level the user has clearly demonstrated (>= 60%),
    // then only fail them out of it if a LOWER level was also clearly failed —
    // a single slip on an easy level doesn't override strong performance above it.
    const levelPercents = { A1: a1, A2: a2, B1: b1, B2: b2, C1: c1, C2: c2 };

    let highestPassed = null;
    for (const level of LEVEL_ORDER) {
        const p = levelPercents[level];
        if (p !== null && p >= 0.6) {
            highestPassed = level;
        }
    }

    if (highestPassed) return highestPassed;

    // Nothing passed at 60%+ — fall back to the lowest level tested, since
    // the user struggled across the board.
    for (const level of LEVEL_ORDER) {
        if (levelPercents[level] !== null) return level;
    }

    return 'A1';
}

const levelDescriptions = {
    A1: { label: 'Beginner', desc: 'You\'re just starting out — perfect! We\'ll build your foundation from scratch with real topics and your first conversations with Gabi.', color: '#E1F5EE', text: '#085041' },
    A2: { label: 'Elementary', desc: 'You know the basics and are ready to build on them. Expect real topics, more vocabulary and your first Gabi conversations.', color: '#E1F5EE', text: '#085041' },
    B1: { label: 'Intermediate', desc: 'You have a solid foundation. Time to unlock more complex grammar, richer vocabulary and confident conversation practice with Gabi.', color: '#E6F1FB', text: '#0C447C' },
    B2: { label: 'Upper Intermediate', desc: 'You\'re getting fluent. We\'ll sharpen your accuracy, expand your vocabulary and prepare you for official exams.', color: '#EEEDFE', text: '#3C3489' },
    C1: { label: 'Advanced', desc: 'You\'re highly proficient. Gabi will challenge you with sophisticated topics, nuanced grammar and near-native conversation.', color: '#FAEEDA', text: '#633806' },
    C2: { label: 'Mastery', desc: 'Outstanding. Welcome to Fluency Mode — unlimited debate, specialist content tracks and advanced cultural immersion.', color: '#FAEEDA', text: '#633806' },
};

const REVIEW_DELAY = 5000;

const speakingPrompts = {
    A1: 'Describe yourself briefly — your name, where you\'re from, and one thing you like.',
    A2: 'Talk about your typical day — what you do in the morning, afternoon and evening.',
    B1: 'Describe a memorable trip or experience you had — what happened, how you felt, and why it mattered to you.',
    B2: 'Talk about a challenge you faced recently — what happened, how you dealt with it, and what you learned from it.',
    C1: 'Share your opinion on a topic you care about. Explain your reasoning, consider a counterargument, and say what would change your mind.',
    C2: 'Tell me about a book, film or idea that changed how you see something. What was it, why did it matter to you, and is there anything about it you still disagree with?',
};

const writingPrompts = {
    A1: 'Write a few sentences about your family.',
    A2: 'Write a short paragraph about your last weekend.',
    B1: 'Write about a hobby you enjoy — what it is, why you like it, and how you got into it.',
    B2: 'Write about a change you would like to make in your life — why, and what has stopped you so far.',
    C1: 'Has technology made daily life better or worse? Explain what you think, then mention one argument someone on the other side might make.',
    C2: 'Should university education be free for everyone? Discuss the arguments for and against, then explain your own opinion.',
};

function PlacementTest() {
    const navigate = useNavigate();
    const location = useLocation();
    const isRetake = !!location.state?.retake;
    const { updateActiveLevel } = useUserLanguage();
    const { interfaceLanguage, t } = useLanguage();
    const targetLangKey = (localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const targetLangName = targetLangKey.charAt(0).toUpperCase() + targetLangKey.slice(1);
    const [questions, setQuestions] = useState(() => buildAdaptiveTest(localStorage.getItem('vozoly_target_language') || 'italian', interfaceLanguage));
    const [questionsTranslated, setQuestionsTranslated] = useState(false);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [selected, setSelected] = useState(null);
    const [finished, setFinished] = useState(false);
    const [result, setResult] = useState(null);
    const [consecutiveWrong, setConsecutiveWrong] = useState(0);
    const [showAbandon, setShowAbandon] = useState(false);
    const [levelAdjustment, setLevelAdjustment] = useState(null);
    const [reviewing, setReviewing] = useState(false);
    const [reviewTimer, setReviewTimer] = useState(REVIEW_DELAY);
    const timerRef = useRef(null);
    const intervalRef = useRef(null);

    const [phase, setPhase] = useState('grammar');
    const [listeningData, setListeningData] = useState(null);
    const [listeningLoading, setListeningLoading] = useState(false);
    const [listeningAttempt, setListeningAttempt] = useState(0);
    const [listeningLevel, setListeningLevel] = useState(null);
    const [listeningAnswers, setListeningAnswers] = useState({});
    const [listeningCurrentQ, setListeningCurrentQ] = useState(0);
    const [listeningRevealed, setListeningRevealed] = useState(false);
    const [listeningPlaying, setListeningPlaying] = useState(false);

    const [speakingLevel, setSpeakingLevel] = useState(null);
    const [speakingHolding, setSpeakingHolding] = useState(false);
    const [speakingLocked, setSpeakingLocked] = useState(false);
    const [speakingRecordSeconds, setSpeakingRecordSeconds] = useState(0);
    const [speakingTranscribing, setSpeakingTranscribing] = useState(false);
    const [, setSpeakingTranscript] = useState('');
    const [speakingResult, setSpeakingResult] = useState(null);
    const [speakingAnalyzing, setSpeakingAnalyzing] = useState(false);
    const speakingRecorderRef = useRef(null);
    const speakingChunksRef = useRef([]);
    const speakingStreamRef = useRef(null);
    const speakingTimerRef = useRef(null);
    const speakingLockedRef = useRef(false);
    const speakingTouchStartXRef = useRef(0);
    const [writingLevel, setWritingLevel] = useState(null);
    const [writingText, setWritingText] = useState('');
    const [writingSubmitting, setWritingSubmitting] = useState(false);
    const [writingResult, setWritingResult] = useState(null);

    useEffect(() => {
        const loadTranslations = async () => {
            if (interfaceLanguage === 'en') {
                setQuestionsTranslated(true);
                return;
            }
            const ids = questions.map(q => q.id);
            const { data } = await supabase
                .from('translated_questions')
                .select('question_id, question_text, options')
                .eq('interface_language', interfaceLanguage)
                .in('question_id', ids);

            if (data && data.length > 0) {
                const translationMap = {};
                data.forEach(row => { translationMap[row.question_id] = row; });
                setQuestions(prev => prev.map(q => {
                    const t = translationMap[q.id];
                    return t ? { ...q, question: t.question_text, options: t.options } : q;
                }));
            }
            setQuestionsTranslated(true);
        };
        loadTranslations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const question = questions[current];
    const totalQuestions = 30;
    const progress = (current / totalQuestions) * 100;

    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            clearInterval(intervalRef.current);
        };
    }, []);

    const startReview = (newAnswers, newConsecutiveWrong) => {
        setReviewing(true);
        setReviewTimer(REVIEW_DELAY);

        intervalRef.current = setInterval(() => {
            setReviewTimer(prev => Math.max(0, prev - 50));
        }, 50);

        timerRef.current = setTimeout(() => {
            clearInterval(intervalRef.current);
            advanceQuestion(newAnswers, newConsecutiveWrong);
        }, REVIEW_DELAY);
    };

    const advanceQuestion = (newAnswers, newConsecutiveWrong) => {
        setReviewing(false);

        // Check if the user has already clearly demonstrated C1/C2 mastery —
        // if so, skip the remaining A1 questions, there's nothing left to learn from them.
        const c1Questions = questions.map((q, i) => ({ q, i })).filter(({ q }) => q.level === 'C1');
        const c2Questions = questions.map((q, i) => ({ q, i })).filter(({ q }) => q.level === 'C2');
        const c1Answered = c1Questions.filter(({ i }) => newAnswers[i] !== undefined);
        const c2Answered = c2Questions.filter(({ i }) => newAnswers[i] !== undefined);
        const c1AllCorrect = c1Answered.length === c1Questions.length && c1Answered.every(({ q, i }) => newAnswers[i] === q.correct);
        const c2AllCorrect = c2Answered.length === c2Questions.length && c2Answered.every(({ q, i }) => newAnswers[i] === q.correct);
        const provenTopLevel = c1AllCorrect && c2AllCorrect;

        // If the user already passed A2 (60%+), the remaining A1 questions can't
        // change their result, so skip them.
        const a2Questions = questions.map((q, i) => ({ q, i })).filter(({ q }) => q.level === 'A2');
        const a2Correct = a2Questions.filter(({ q, i }) => newAnswers[i] === q.correct).length;
        const a2Passed = a2Questions.length > 0 && a2Correct / a2Questions.length >= 0.6;
        const nextIsA1 = questions[current + 1]?.level === 'A1';

        const shouldFinish =
            current >= totalQuestions - 1 ||
            (newConsecutiveWrong >= 3 && current >= 10) ||
            provenTopLevel ||
            (nextIsA1 && a2Passed);

        if (shouldFinish) {
            setAnswers(newAnswers);
            startListeningPhaseWithAnswers(newAnswers);
        } else {
            setCurrent(current + 1);
            setSelected(null);
        }
    };

    const startListeningPhaseWithAnswers = (finalAnswers) => {
        const grammarLevel = calculateAdaptiveLevel(finalAnswers, questions);
        setListeningLevel(grammarLevel);
        setListeningAttempt(1);
        setPhase('listening');
        loadListeningPassage(grammarLevel);
    };

    const handleNext = () => {
        if (reviewing) return;
        const isCorrect = selected === question.correct;
        const newAnswers = { ...answers, [current]: selected };
        setAnswers(newAnswers);
        const newConsecutiveWrong = isCorrect ? 0 : consecutiveWrong + 1;
        setConsecutiveWrong(newConsecutiveWrong);
        startReview(newAnswers, newConsecutiveWrong);
    };

    const handleChangeAnswer = () => {
        clearTimeout(timerRef.current);
        clearInterval(intervalRef.current);
        setReviewing(false);
        setSelected(answers[current] !== undefined ? answers[current] : null);
        setAnswers(prev => {
            const updated = { ...prev };
            delete updated[current];
            return updated;
        });
    };

    const loadListeningPassage = async (level) => {
        setListeningLoading(true);
        setListeningAnswers({});
        setListeningCurrentQ(0);
        setListeningRevealed(false);
        try {
            const response = await fetch('/api/generate-listening', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ level, language: targetLangKey }),
            });
            const data = await response.json();
            setListeningData(data);
        } catch (err) {
            setListeningData(null);
        }
        setListeningLoading(false);
    };

    const playListeningPassage = async () => {
        if (!listeningData) return;
        setListeningPlaying(true);
        await speakWithLuna(listeningData.passage, () => setListeningPlaying(false));
    };

    const submitListeningAnswer = () => {
        setListeningRevealed(true);
    };

    const nextListeningQuestion = () => {
        const nextQ = listeningCurrentQ + 1;
        if (nextQ >= (listeningData?.questions?.length || 3)) {
            finishListeningPhase();
        } else {
            setListeningCurrentQ(nextQ);
            setListeningRevealed(false);
        }
    };

    const finishListeningPhase = () => {
        const questions = listeningData?.questions || [];
        const correctCount = questions.filter((q, i) => listeningAnswers[i] === q.correct).length;
        const passed = questions.length === 0 || correctCount >= 2;

        if (passed || listeningAttempt >= 2) {
            const finalListeningLevel = passed
                ? listeningLevel
                : LEVEL_ORDER[Math.max(0, LEVEL_ORDER.indexOf(listeningLevel) - 1)];
            stopLunaVoice();
            setSpeakingLevel(finalListeningLevel);
            setPhase('speaking');
        } else {
            const droppedLevel = LEVEL_ORDER[Math.max(0, LEVEL_ORDER.indexOf(listeningLevel) - 1)];
            setListeningLevel(droppedLevel);
            setListeningAttempt(2);
            loadListeningPassage(droppedLevel);
        }
    };

    const continueAfterListening = () => {
        // Used only as a fallback when the passage failed to load entirely
        stopLunaVoice();
        setSpeakingLevel(listeningLevel);
        setPhase('speaking');
    };

    const startSpeakingRecording = async (clientX) => {
        speakingLockedRef.current = false;
        setSpeakingLocked(false);
        speakingTouchStartXRef.current = clientX || 0;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            speakingStreamRef.current = stream;
            speakingChunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) speakingChunksRef.current.push(e.data);
            };
            speakingRecorderRef.current = recorder;
            recorder.start();
            setSpeakingHolding(true);
            setSpeakingRecordSeconds(0);
            speakingTimerRef.current = setInterval(() => setSpeakingRecordSeconds(s => s + 1), 1000);
        } catch (err) {
            // Microphone unavailable — allow skipping ahead
        }
    };

    const handleSpeakingTouchMove = (e) => {
        if (!speakingHolding || speakingLockedRef.current) return;
        const currentX = e.touches?.[0]?.clientX || 0;
        if (speakingTouchStartXRef.current - currentX > 60) {
            speakingLockedRef.current = true;
            setSpeakingLocked(true);
        }
    };

    const stopSpeakingRecording = async () => {
        if (speakingLockedRef.current) return;
        await stopSpeakingRecordingCore();
    };

    const stopSpeakingRecordingCore = async () => {
        setSpeakingHolding(false);
        clearInterval(speakingTimerRef.current);
        const recorder = speakingRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') return;

        const blob = await new Promise((resolve) => {
            recorder.onstop = () => {
                speakingStreamRef.current?.getTracks().forEach(t => t.stop());
                resolve(new Blob(speakingChunksRef.current, { type: recorder.mimeType }));
            };
            recorder.stop();
        });

        if (blob.size < 1000) return;
        setSpeakingTranscribing(true);

        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            const createRes = await fetch('/api/transcribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audio: base64, language: targetLangKey }),
            });
            const { id } = await createRes.json();

            let text = '';
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1200));
                const pollRes = await fetch(`/api/transcribe?id=${id}`);
                const poll = await pollRes.json();
                if (poll.status === 'completed') { text = poll.text; break; }
                if (poll.status === 'error') break;
            }

            setSpeakingTranscript(text);
            setSpeakingTranscribing(false);

            if (text.trim()) {
                setSpeakingAnalyzing(true);
                const analysisRes = await fetch('/api/luna-chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messages: [], mode: 'analyze-speaking', transcript: text, estimatedLevel: speakingLevel, language: targetLangName }),
                });
                const analysis = await analysisRes.json();
                setSpeakingResult(analysis);
                setSpeakingAnalyzing(false);
            }
        } catch (err) {
            setSpeakingTranscribing(false);
            setSpeakingAnalyzing(false);
        }
    };

    const finishLockedSpeaking = () => {
        speakingLockedRef.current = false;
        setSpeakingLocked(false);
        stopSpeakingRecordingCore();
    };

    const cancelLockedSpeaking = () => {
        speakingLockedRef.current = false;
        setSpeakingLocked(false);
        setSpeakingHolding(false);
        clearInterval(speakingTimerRef.current);
        try { speakingRecorderRef.current?.stop(); } catch (e) { }
        speakingStreamRef.current?.getTracks().forEach(t => t.stop());
        speakingChunksRef.current = [];
    };

    const skipSpeaking = () => {
        setSpeakingResult({ level: speakingLevel, confidence: 'low', reasoning: 'Skipped — using listening/grammar estimate.' });
    };

    const continueAfterSpeaking = () => {
        const finalLevel = speakingResult?.level || speakingLevel;
        setWritingLevel(finalLevel);
        setPhase('writing');
    };

    const submitWriting = async () => {
        if (!writingText.trim()) {
            setWritingResult({ level: writingLevel, confidence: 'low', reasoning: 'Skipped — using prior estimate.' });
            return;
        }
        setWritingSubmitting(true);
        try {
            const response = await fetch('/api/review-writing', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: writingText.trim(),
                    language: targetLangName,
                    mode: 'assess-level',
                    estimatedLevel: writingLevel,
                }),
            });
            const data = await response.json();
            setWritingResult(data);
        } catch (err) {
            setWritingResult({ level: writingLevel, confidence: 'low', reasoning: 'Analysis unavailable — using prior estimate.' });
        }
        setWritingSubmitting(false);
    };

    const skipWriting = () => {
        setWritingResult({ level: writingLevel, confidence: 'low', reasoning: 'Skipped — using prior estimate.' });
    };

    const continueAfterWriting = () => {
        const finalLevel = writingResult?.level || writingLevel;
        setResult(finalLevel);
        setFinished(true);
        setPhase('grammar');
    };

    if (phase === 'speaking') {
        return (
            <div className="pt-screen">
                <div className="pt-top">
                    <button className="pt-back" onClick={() => setShowAbandon(true)}>✕</button>
                    <div className="pt-calibrating">{t('onboarding.placementTest.speakingCheck')}</div>
                    <div className="pt-level-badge">{speakingLevel}</div>
                </div>

                <div className="pt-content">
                    <div className="pt-listening-intro">{t('onboarding.placementTest.speakingIntro')}</div>

                    <div className="pt-speaking-prompt">
                        <div className="pt-speaking-prompt-label">{t('onboarding.placementTest.yourPrompt')}</div>
                        <div className="pt-speaking-prompt-text">{speakingPrompts[speakingLevel] || speakingPrompts.A1}</div>
                    </div>

                    {speakingResult ? (
                        <div className="pt-speaking-done">
                            <div className="pt-speaking-done-icon">✓</div>
                            <div className="pt-speaking-done-text">{t('onboarding.placementTest.gotItSpeaking')}</div>
                        </div>
                    ) : speakingAnalyzing ? (
                        <div className="pt-listening-loading">{t('onboarding.placementTest.analyzingSpeaking')}</div>
                    ) : speakingTranscribing ? (
                        <div className="pt-listening-loading">{t('onboarding.placementTest.transcribing')}</div>
                    ) : (
                        <div className="pt-speaking-record-wrap">
                            {speakingLocked ? (
                                <div className="pt-speaking-locked-controls">
                                    <button className="pt-speaking-cancel-btn" onClick={cancelLockedSpeaking}>✕</button>
                                    <div className="pt-speaking-locked-indicator">
                                        🔴 {Math.floor(speakingRecordSeconds / 60)}:{String(speakingRecordSeconds % 60).padStart(2, '0')}
                                    </div>
                                    <button className="pt-speaking-locked-send-btn" onClick={finishLockedSpeaking}>➤</button>
                                </div>
                            ) : (
                                <>
                                    <button
                                        className={`pt-speaking-mic ${speakingHolding ? 'recording' : ''}`}
                                        onMouseDown={(e) => startSpeakingRecording(e.clientX)}
                                        onMouseUp={stopSpeakingRecording}
                                        onTouchStart={(e) => { e.preventDefault(); startSpeakingRecording(e.touches[0].clientX); }}
                                        onTouchMove={handleSpeakingTouchMove}
                                        onTouchEnd={(e) => { e.preventDefault(); stopSpeakingRecording(); }}
                                    >
                                        {speakingHolding ? '🔴' : '🎤'}
                                    </button>
                                    <div className="pt-speaking-hint">
                                        {speakingHolding
                                            ? `${Math.floor(speakingRecordSeconds / 60)}:${String(speakingRecordSeconds % 60).padStart(2, '0')} · ◀ ${t('onboarding.placementTest.slideToLock')} · ${t('onboarding.placementTest.releaseToStop')}`
                                            : t('onboarding.placementTest.holdToSpeak')}
                                    </div>
                                    <button className="pt-speaking-skip" onClick={skipSpeaking}>{t('onboarding.placementTest.skipStep')} →</button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="pt-footer">
                    {speakingResult && (
                        <button className="pt-next-btn" onClick={continueAfterSpeaking}>
                            {t('onboarding.placementTest.continue')} →
                        </button>
                    )}
                </div>

                {showAbandon && (
                    <div className="pt-abandon-overlay">
                        <div className="pt-abandon-card">
                            <div className="pt-abandon-title">{t('onboarding.placementTest.abandonTitle')}</div>
                            <div className="pt-abandon-desc">
                                {t('onboarding.placementTest.abandonDesc')}
                            </div>
                            <div className="pt-abandon-actions">
                                <button className="pt-abandon-cancel" onClick={() => setShowAbandon(false)}>
                                    {t('onboarding.placementTest.keepGoing')}
                                </button>
                                <button className="pt-abandon-confirm" onClick={() => navigate(isRetake ? '/profile' : '/onboarding/path')}>
                                    {t('onboarding.placementTest.abandon')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (phase === 'writing') {
        return (
            <div className="pt-screen">
                <div className="pt-top">
                    <button className="pt-back" onClick={() => setShowAbandon(true)}>✕</button>
                    <div className="pt-calibrating">{t('onboarding.placementTest.writingCheck')}</div>
                    <div className="pt-level-badge">{writingLevel}</div>
                </div>

                <div className="pt-content">
                    <div className="pt-listening-intro">{t('onboarding.placementTest.writingIntro')}</div>

                    <div className="pt-speaking-prompt">
                        <div className="pt-speaking-prompt-label">{t('onboarding.placementTest.yourPrompt')}</div>
                        <div className="pt-speaking-prompt-text">{writingPrompts[writingLevel] || writingPrompts.A1}</div>
                    </div>

                    {writingResult ? (
                        <div className="pt-speaking-done">
                            <div className="pt-speaking-done-icon">✓</div>
                            <div className="pt-speaking-done-text">{t('onboarding.placementTest.gotItWriting')}</div>
                        </div>
                    ) : writingSubmitting ? (
                        <div className="pt-listening-loading">{t('onboarding.placementTest.analyzingWriting')}</div>
                    ) : (
                        <>
                            <textarea
                                className="pt-writing-textarea"
                                placeholder={t('onboarding.placementTest.writeHere')}
                                value={writingText}
                                onChange={e => setWritingText(e.target.value)}
                                rows={6}
                            />
                            <button className="pt-speaking-skip" onClick={skipWriting}>{t('onboarding.placementTest.skipStep')} →</button>
                        </>
                    )}
                </div>

                <div className="pt-footer">
                    {writingResult ? (
                        <button className="pt-next-btn" onClick={continueAfterWriting}>
                            {t('onboarding.placementTest.seeMyResult')} →
                        </button>
                    ) : !writingSubmitting ? (
                        <button className="pt-next-btn" disabled={!writingText.trim()} onClick={submitWriting}>
                            {t('onboarding.placementTest.submitWriting')} →
                        </button>
                    ) : null}
                </div>

                {showAbandon && (
                    <div className="pt-abandon-overlay">
                        <div className="pt-abandon-card">
                            <div className="pt-abandon-title">{t('onboarding.placementTest.abandonTitle')}</div>
                            <div className="pt-abandon-desc">
                                {t('onboarding.placementTest.abandonDesc')}
                            </div>
                            <div className="pt-abandon-actions">
                                <button className="pt-abandon-cancel" onClick={() => setShowAbandon(false)}>
                                    {t('onboarding.placementTest.keepGoing')}
                                </button>
                                <button className="pt-abandon-confirm" onClick={() => navigate(isRetake ? '/profile' : '/onboarding/path')}>
                                    {t('onboarding.placementTest.abandon')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (phase === 'listening') {
        const currentQuestion = listeningData?.questions?.[listeningCurrentQ];
        const totalListeningQs = listeningData?.questions?.length || 3;

        return (
            <div className="pt-screen">
                <div className="pt-top">
                    <button className="pt-back" onClick={() => { stopLunaVoice(); setShowAbandon(true); }}>✕</button>
                    <div className="pt-calibrating">{t('onboarding.placementTest.listeningCheck')}</div>
                    <div className="pt-level-badge">{listeningLevel}</div>
                </div>

                <div className="pt-content">
                    {listeningLoading ? (
                        <div className="pt-listening-loading">{t('onboarding.placementTest.preparingListening')}</div>
                    ) : listeningData && currentQuestion ? (
                        <>
                            <div className="pt-listening-intro">{t('onboarding.placementTest.listenCarefully').replace('{count}', totalListeningQs)}</div>
                            <button
                                className="pt-listening-play"
                                onClick={playListeningPassage}
                                disabled={listeningPlaying}
                            >
                                {listeningPlaying ? `🔊 ${t('onboarding.placementTest.playing')}` : `▶ ${t('onboarding.placementTest.playAudio')}`}
                            </button>

                            <div className="pt-question-num" style={{ marginTop: 28 }}>{t('onboarding.placementTest.question')} {listeningCurrentQ + 1} {t('onboarding.placementTest.of')} {totalListeningQs}</div>
                            <div className="pt-question">{currentQuestion.question}</div>

                            <div className="pt-options">
                                {currentQuestion.options.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`pt-option
                                            ${listeningAnswers[listeningCurrentQ] === index && !listeningRevealed ? 'selected' : ''}
                                            ${listeningRevealed && index === currentQuestion.correct ? 'answered' : ''}
                                            ${listeningRevealed && index !== currentQuestion.correct ? 'faded' : ''}
                                        `}
                                        onClick={() => !listeningRevealed && setListeningAnswers(prev => ({ ...prev, [listeningCurrentQ]: index }))}
                                    >
                                        <div className={`pt-option-letter ${listeningAnswers[listeningCurrentQ] === index && !listeningRevealed ? 'selected' : ''} ${listeningRevealed && index === currentQuestion.correct ? 'answered' : ''}`}>
                                            {['A', 'B', 'C', 'D'][index]}
                                        </div>
                                        <div className="pt-option-text">{option}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="pt-listening-loading">{t('onboarding.placementTest.listeningError')}</div>
                    )}
                </div>

                <div className="pt-footer">
                    {!listeningData || !currentQuestion ? (
                        <button className="pt-next-btn" onClick={continueAfterListening}>
                            {t('onboarding.placementTest.continue')} →
                        </button>
                    ) : !listeningRevealed ? (
                        <button
                            className="pt-next-btn"
                            disabled={listeningAnswers[listeningCurrentQ] === undefined}
                            onClick={submitListeningAnswer}
                        >
                            {t('onboarding.placementTest.checkAnswer')} →
                        </button>
                    ) : (
                        <button className="pt-next-btn" onClick={nextListeningQuestion}>
                            {listeningCurrentQ + 1 >= totalListeningQs ? `${t('onboarding.placementTest.continue')} →` : `${t('onboarding.placementTest.nextQuestion')} →`}
                        </button>
                    )}
                </div>

                {showAbandon && (
                    <div className="pt-abandon-overlay">
                        <div className="pt-abandon-card">
                            <div className="pt-abandon-title">{t('onboarding.placementTest.abandonTitle')}</div>
                            <div className="pt-abandon-desc">
                                {t('onboarding.placementTest.abandonDesc')}
                            </div>
                            <div className="pt-abandon-actions">
                                <button className="pt-abandon-cancel" onClick={() => setShowAbandon(false)}>
                                    Keep going
                                </button>
                                <button className="pt-abandon-confirm" onClick={() => navigate(isRetake ? '/profile' : '/onboarding/path')}>
                                    Abandon
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (finished && result) {
        const desc = levelDescriptions[result];
        const correctCount = Object.entries(answers).filter(
            ([i, a]) => a === questions[parseInt(i)].correct
        ).length;

        if (levelAdjustment === null) {
            return (
                <div className="pt-screen">
                    <div className="pt-result">
                        <div className="pt-result-icon" style={{ background: desc.color }}>🎯</div>
                        <div className="pt-result-label" style={{ color: desc.text }}>{t('onboarding.placementTest.yourLevel')}</div>
                        <div className="pt-result-level" style={{ color: desc.text }}>{result}</div>
                        <div className="pt-result-name" style={{ color: desc.text }}>{desc.label}</div>
                        <div className="pt-result-desc">{desc.desc}</div>

                        <div className="pt-result-stats">
                            <div className="pt-stat">
                                <div className="pt-stat-value">{correctCount}</div>
                                <div className="pt-stat-label">{t('onboarding.placementTest.correct')}</div>
                            </div>
                            <div className="pt-stat">
                                <div className="pt-stat-value">{Object.keys(answers).length}</div>
                                <div className="pt-stat-label">{t('onboarding.placementTest.answered')}</div>
                            </div>
                            <div className="pt-stat">
                                <div className="pt-stat-value">{result}</div>
                                <div className="pt-stat-label">{t('onboarding.placementTest.placedAt')}</div>
                            </div>
                        </div>

                        <div className="pt-disclaimer">
                            📝 {t('onboarding.placementTest.resultDisclaimer')}
                        </div>

                        <div className="pt-feels-right">
                            <div className="pt-feels-title">{t('onboarding.placementTest.doesThisFeel')}</div>
                            <div className="pt-feels-options">
                                <button
                                    className="pt-feels-btn pt-feels-yes"
                                    onClick={() => setLevelAdjustment('same')}
                                >
                                    ✓ {t('onboarding.placementTest.feelsAccurate')}
                                </button>
                                <button
                                    className="pt-feels-btn pt-feels-lower"
                                    onClick={() => setLevelAdjustment('lower')}
                                >
                                    ↓ {t('onboarding.placementTest.lowerLevel')}
                                </button>
                                <button
                                    className="pt-feels-btn pt-feels-higher"
                                    onClick={() => setLevelAdjustment('higher')}
                                >
                                    ↑ {t('onboarding.placementTest.higherLevel')}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            );
        }

        const finalLevel = levelAdjustment === 'lower'
            ? LEVEL_ORDER[Math.max(0, LEVEL_ORDER.indexOf(result) - 1)]
            : levelAdjustment === 'higher'
                ? LEVEL_ORDER[Math.min(LEVEL_ORDER.length - 1, LEVEL_ORDER.indexOf(result) + 1)]
                : result;

        const finalDesc = levelDescriptions[finalLevel];

        return (
            <div className="pt-screen">
                <div className="pt-result">
                    <div className="pt-result-icon" style={{ background: finalDesc.color }}>🎯</div>
                    <div className="pt-result-label" style={{ color: finalDesc.text }}>
                        {levelAdjustment === 'same' ? t('onboarding.placementTest.yourLevel') : t('onboarding.placementTest.adjustedLevel')}
                    </div>
                    <div className="pt-result-level" style={{ color: finalDesc.text }}>{finalLevel}</div>
                    <div className="pt-result-name" style={{ color: finalDesc.text }}>{finalDesc.label}</div>
                    <div className="pt-result-desc">{finalDesc.desc}</div>

                    {levelAdjustment !== 'same' && (
                        <div className="pt-adjusted-note">
                            {t('onboarding.placementTest.adjustedFrom').replace('{from}', result).replace('{to}', finalLevel)}
                            {' '}{t('onboarding.placementTest.changeAnytime')}
                        </div>
                    )}

                    <div className="pt-disclaimer">
                        📝 {t('onboarding.placementTest.resultDisclaimer')}
                    </div>

                    <div className="pt-adjust-note">
                        💡 {t('onboarding.placementTest.retakeNote')}
                    </div>

                    <button className="pt-result-btn" onClick={async () => {
                        localStorage.setItem('vozoly_level', finalLevel);
                        if (isRetake) {
                            const { data: { user } } = await supabase.auth.getUser();
                            if (user) {
                                await supabase
                                    .from('profiles')
                                    .update({ level: finalLevel })
                                    .eq('id', user.id);
                                await updateActiveLevel(finalLevel);
                            }
                            navigate('/profile');
                            return;
                        }
                        navigate('/signup');
                    }}>
                        {t('onboarding.placementTest.saveResultsBtn')} →
                    </button>
                </div>
            </div>
        );
    }

    if (!questionsTranslated) {
        return (
            <div className="pt-screen">
                <div className="pt-top">
                    <button className="pt-back" onClick={() => setShowAbandon(true)}>✕</button>
                    <div className="pt-calibrating">{t('onboarding.placementTest.preparingTest')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '60px 20px' }}>
                    <div className="pt-listening-loading">{t('onboarding.placementTest.loading')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-screen">

            <div className="pt-top">
                <button className="pt-back" onClick={() => !reviewing && setShowAbandon(true)}>✕</button>
                <div className="pt-calibrating">{t('onboarding.placementTest.calibrating')}</div>
                <div className="pt-level-badge">{question?.level}</div>
            </div>

            <div className="pt-progress-bar">
                <div className="pt-progress-fill" style={{ width: `${progress}%` }}></div>
            </div>

            {reviewing && (
                <div className="pt-review-bar">
                    <div
                        className="pt-review-fill"
                        style={{ width: `${(reviewTimer / REVIEW_DELAY) * 100}%` }}
                    ></div>
                </div>
            )}

            <div className="pt-content">
                <div className="pt-question-num">{t('onboarding.placementTest.questionNum')} {current + 1}</div>
                <div className="pt-question">{question?.question}</div>

                <div className="pt-options">
                    {question?.options.map((option, index) => (
                        <div
                            key={index}
                            className={`pt-option
                ${selected === index && !reviewing ? 'selected' : ''}
                ${reviewing && selected === index ? 'answered' : ''}
                ${reviewing && index !== selected ? 'faded' : ''}
              `}
                            onClick={() => !reviewing && setSelected(index)}
                        >
                            <div className={`pt-option-letter
                ${selected === index && !reviewing ? 'selected' : ''}
                ${reviewing && selected === index ? 'answered' : ''}
              `}>
                                {['A', 'B', 'C', 'D'][index]}
                            </div>
                            <div className="pt-option-text">{option}</div>
                        </div>
                    ))}
                </div>

                {reviewing && (
                    <div className="pt-review-hint">
                        <span>{t('onboarding.placementTest.movingOn')}</span>
                        <button className="pt-change-answer-inline" onClick={handleChangeAnswer}>
                            ← {t('onboarding.placementTest.changeAnswer')}
                        </button>
                    </div>
                )}
            </div>

            <div className="pt-footer">
                {!reviewing ? (
                    <button
                        className="pt-next-btn"
                        disabled={selected === null}
                        onClick={handleNext}
                    >
                        {current < totalQuestions - 1 ? `${t('onboarding.placementTest.next')} →` : `${t('onboarding.placementTest.seeMyResultGrammar')} →`}
                    </button>
                ) : (
                    <button className="pt-next-btn-skip" onClick={() => {
                        clearTimeout(timerRef.current);
                        clearInterval(intervalRef.current);
                        advanceQuestion({ ...answers }, consecutiveWrong);
                    }}>
                        {t('onboarding.placementTest.skipWait')} →
                    </button>
                )}
            </div>

            {showAbandon && (
                <div className="pt-abandon-overlay">
                    <div className="pt-abandon-card">
                        <div className="pt-abandon-title">{t('onboarding.placementTest.abandonTitle')}</div>
                        <div className="pt-abandon-desc">
                            {t('onboarding.placementTest.abandonDesc')}
                        </div>
                        <div className="pt-abandon-actions">
                            <button className="pt-abandon-cancel" onClick={() => setShowAbandon(false)}>
                                {t('onboarding.placementTest.keepGoing')}
                            </button>
                            <button className="pt-abandon-confirm" onClick={() => navigate(isRetake ? '/profile' : '/onboarding/path')}>
                                Abandon
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default PlacementTest;